import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import React from 'react';
import { CopyBlock, dracula, atomOneDark, nord } from 'react-code-blocks';

// Shared component for "Coming Soon" message
const ComingSoonSection: React.FC = () => (
    <Box sx={{ 
        textAlign: 'center', 
        py: 6, 
        px: 4,
        bgcolor: 'rgba(11, 52, 82, 0.03)',
        borderRadius: '8px',
        border: '1px dashed rgba(11, 52, 82, 0.2)'
    }}>
        <Typography sx={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            color: '#0b3452', 
            mb: 2 
        }}>
            🚀 Coming Soon
        </Typography>
        <Typography sx={{ 
            color: '#666', 
            fontSize: '1rem',
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.7
        }}>
            Event tracking documentation for this platform is currently under development. 
            We're working hard to bring you comprehensive guides and examples. 
            Stay tuned for updates!
        </Typography>
    </Box>
);

// Android Event Tracking Component
export const AndroidEventTracking: React.FC = () => {
    return (
        <Box>
            {/* Overview */}
            <Typography variant="h6" sx={{ mt: 0, color: "#0b3452", fontWeight: "bold" }}>
                Overview
            </Typography>
            <Typography sx={{ mt: 2, mb: 3, color: "#555", lineHeight: 1.7 }}>
                The Finvu Android SDK provides a comprehensive event tracking system that allows you to:
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Monitor SDK user journeys (login, discovery, linking, consent)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events in your application</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Integrate with analytics platforms</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Debug and troubleshoot user flows</Typography></li>
            </Box>
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3 
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Event tracking is <strong>disabled by default</strong>. You must enable it explicitly using <code>setEventsEnabled(true)</code>.
                </Typography>
            </Box>

            {/* Getting Started */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Getting Started
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Step 1: Enable Event Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Event tracking must be enabled before events will be tracked:
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.FinvuManager

// Enable event tracking
FinvuManager.shared.setEventsEnabled(true)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Step 2: Create an Event Listener
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Create a class that implements the <code>FinvuEventListener</code> interface:
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.publicInterface.FinvuEvent
import com.finvu.android.publicInterface.FinvuEventListener
import com.finvu.android.publicInterface.FinvuEventType

class MyEventListener : FinvuEventListener {
    override fun onEvent(event: FinvuEvent) {
        println("📊 Event: \${event.eventName}")
        println("   Category: \${event.eventCategory}")
        println("   Timestamp: \${event.timestamp}")
        
        // Access count from params
        val count = event.getParam<Int>("count")
        if (count != null) {
            println("   Count: $count")
        }
        
        // Access event parameters
        println("   Params: \${event.params}")
        
        // Handle specific events using enum (recommended)
        when (event.eventName) {
            FinvuEventType.LOGIN_OTP_VERIFIED.eventName -> {
                println("   → User logged in successfully")
                // Send to analytics
                sendToAnalytics(event)
            }
            FinvuEventType.CONSENT_APPROVED.eventName -> {
                println("   → Consent approved")
                // Track conversion
                trackConversion(event)
            }
        }
    }
    
    private fun sendToAnalytics(event: FinvuEvent) {
        // Integrate with your analytics platform
        // Example: Firebase, Mixpanel, Amplitude, etc.
    }
    
    private fun trackConversion(event: FinvuEvent) {
        // Track business metrics
    }
}`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Step 3: Add the Event Listener
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Add your listener to receive events:
            </Typography>
            <CopyBlock
                text={`val eventListener = MyEventListener()
FinvuManager.shared.addEventListener(eventListener)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Complete Setup Example */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Setup Example: Application Class
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                For a complete setup in your Application class:
            </Typography>
            <CopyBlock
                text={`import android.app.Application
import android.util.Log
import com.finvu.android.FinvuManager
import com.finvu.android.publicInterface.FinvuEvent
import com.finvu.android.publicInterface.FinvuEventListener

class DemoApplication : Application() {
    
    private val eventListener = object : FinvuEventListener {
        override fun onEvent(event: FinvuEvent) {
            Log.d("FinvuEvents", "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
            Log.d("FinvuEvents", "Event: \${event.eventName}")
            Log.d("FinvuEvents", "Category: \${event.eventCategory}")
            Log.d("FinvuEvents", "Timestamp: \${event.timestamp}")
            
            event.params.forEach { (key, value) ->
                Log.d("FinvuEvents", "  $key: $value")
            }
            Log.d("FinvuEvents", "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
        }
    }
    
    override fun onCreate() {
        super.onCreate()
        
        // Add event listener
        FinvuManager.shared.addEventListener(eventListener)
        
        // Enable event tracking
        FinvuManager.shared.setEventsEnabled(true)
        
        Log.d("DemoApp", "✅ Finvu event tracking enabled")
    }
    
    override fun onTerminate() {
        // Remove listener when app terminates
        FinvuManager.shared.removeEventListener(eventListener)
        super.onTerminate()
    }
}`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Available Events */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Available Events
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                The SDK provides a comprehensive set of events organized by category. All events are defined in the <code>FinvuEventType</code> enum:
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                WebSocket Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>WEBSOCKET_CONNECTED</code> - WebSocket connection established</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>WEBSOCKET_DISCONNECTED</code> - WebSocket connection closed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Redirection Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_REQUEST_VALID</code> - Consent request is valid</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_REQUEST_INVALID</code> - Consent request is invalid</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Authentication Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_INITIATED</code> - Login process started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_GENERATED</code> - OTP generated for login</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_FAILED</code> - OTP generation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_LOCKED</code> - OTP attempts locked</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_VERIFIED</code> - OTP verified successfully</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_NOT_VERIFIED</code> - OTP verification failed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Discovery Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>DISCOVERY_INITIATED</code> - Account discovery started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>ACCOUNTS_DISCOVERED</code> - Accounts discovered successfully</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>DISCOVERY_FAILED</code> - Account discovery failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>ACCOUNTS_NOT_DISCOVERED</code> - No accounts discovered</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Linking Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_INITIATED</code> - Account linking started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_OTP_GENERATED</code> - OTP generated for linking</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_OTP_FAILED</code> - Linking OTP generation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_SUCCESS</code> - Account linking succeeded</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_FAILURE</code> - Account linking failed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Consent Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LINKED_ACCOUNTS_SUMMARY</code> - Linked accounts summary received</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_APPROVED</code> - Consent approved by user</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_DENIED</code> - Consent denied by user</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>APPROVE_CONSENT_FAILED</code> - Consent approval failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_HANDLE_FAILED</code> - Consent handle operation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>GET_CONSENT_STATUS_FAILED</code> - Failed to get consent status</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Error Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>SESSION_ERROR</code> - Session error occurred</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>SESSION_FAILURE</code> - Session failure occurred</Typography></li>
            </Box>

            {/* Event Parameters Reference */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Parameters Reference
            </Typography>
            <Typography sx={{ mt: 2, mb: 3, color: "#555" }}>
                All events have a <code>count</code> parameter (Int) in <code>params</code> that tracks how many times the event has occurred. Access it via <code>event.params["count"]</code> or <code>event.getParam&lt;Int&gt;("count")</code>.
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                For <code>ACCOUNTS_DISCOVERED</code>, <code>LINKING_SUCCESS</code>, and <code>LINKED_ACCOUNTS_SUMMARY</code>, the <code>count</code> in params represents the number of accounts (discovered/linked), not the event occurrence count.
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                For <code>LINKED_ACCOUNTS_SUMMARY</code>, <code>fips</code> and <code>fiTypes</code> lists are only included if they are populated (non-empty). The <code>fips</code> list contains unique FIP names (not FIP IDs) sorted alphabetically, and <code>fiTypes</code> contains unique FI types sorted alphabetically from the linked accounts.
            </Typography>
            <Typography sx={{ mb: 3, color: "#555" }}>
                All event parameters are automatically sanitized to remove any potential PII (Personally Identifiable Information). Additional parameters listed below are only included when relevant to the specific event.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                WebSocket Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>WEBSOCKET_CONNECTED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>WEBSOCKET_DISCONNECTED</code></TableCell>
                            <TableCell><code>count</code> (Int), <code>code</code> (Int, optional), <code>reason</code> (String, optional)</TableCell>
                            <TableCell>Event occurrence count + WebSocket close code and reason</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Redirection Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>CONSENT_REQUEST_VALID</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_REQUEST_INVALID</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Authentication Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LOGIN_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_GENERATED</code></TableCell>
                            <TableCell><code>count</code> (Int), <code>authType</code> (String, optional)</TableCell>
                            <TableCell>Event count + auth type used (e.g., "OTP")</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_FAILED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_LOCKED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_VERIFIED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_NOT_VERIFIED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Discovery Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>DISCOVERY_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>ACCOUNTS_DISCOVERED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Number of accounts discovered (note: this overrides the event occurrence count in params)</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>DISCOVERY_FAILED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>ACCOUNTS_NOT_DISCOVERED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Linking Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LINKING_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (Int), <code>fipId</code> (String, optional)</TableCell>
                            <TableCell>Event occurrence count + FIP ID</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_OTP_GENERATED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_OTP_FAILED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_SUCCESS</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Number of accounts linked (note: this overrides the event occurrence count in params)</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_FAILURE</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Consent Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LINKED_ACCOUNTS_SUMMARY</code></TableCell>
                            <TableCell><code>count</code> (Int), <code>fips</code> (List&lt;String&gt;, optional), <code>fiTypes</code> (List&lt;String&gt;, optional)</TableCell>
                            <TableCell>Number of linked accounts (note: this overrides the event occurrence count in params) + unique FIP names (not FIP IDs) and unique FI types from linked accounts. Should be tracked when linked accounts are fetched successfully.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_APPROVED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_DENIED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>APPROVE_CONSENT_FAILED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_HANDLE_FAILED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>GET_CONSENT_STATUS_FAILED</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Error Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>SESSION_ERROR</code></TableCell>
                            <TableCell><code>count</code> (Int), <code>error</code> (String, optional)</TableCell>
                            <TableCell>Event occurrence count + error message if available</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>SESSION_FAILURE</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ 
                bgcolor: 'rgba(11, 52, 82, 0.05)', 
                border: '1px solid rgba(11, 52, 82, 0.2)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 3
            }}>
                <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                    📝 Note
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>To access parameters, use <code>event.params["key"]</code> or <code>event.getParam&lt;Type&gt;("key")</code>.</Typography></li>
                </Box>
            </Box>

            {/* Adding Event Listeners */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Adding Event Listeners
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                You can add multiple event listeners. Each listener will receive all events:
            </Typography>
            <CopyBlock
                text={`// Analytics listener
class AnalyticsListener : FinvuEventListener {
    override fun onEvent(event: FinvuEvent) {
        // Send to analytics platform
        Analytics.track(event.eventName, event.params)
    }
}

// Debug listener
class DebugListener : FinvuEventListener {
    override fun onEvent(event: FinvuEvent) {
        // Log for debugging
        Log.d("SDK", "🔍 [DEBUG] \${event.eventName} at \${event.timestamp}")
    }
}

// Add both listeners
val analyticsListener = AnalyticsListener()
val debugListener = DebugListener()

FinvuManager.shared.addEventListener(analyticsListener)
FinvuManager.shared.addEventListener(debugListener)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Removing Event Listeners */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Removing Event Listeners
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                To stop receiving events, remove the listener:
            </Typography>
            <CopyBlock
                text={`// Remove a specific listener
FinvuManager.shared.removeEventListener(eventListener)

// Example: Remove listener when activity is destroyed
class MainActivity : AppCompatActivity() {
    private val eventListener = MyEventListener()
    
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        FinvuManager.shared.addEventListener(eventListener)
    }
    
    override fun onDestroy() {
        super.onDestroy()
        // Remove listener to prevent memory leaks
        FinvuManager.shared.removeEventListener(eventListener)
    }
}`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Always remove listeners when they're no longer needed to prevent memory leaks.
                </Typography>
            </Box>
            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Disabling Event Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                To completely disable event tracking:
            </Typography>
            <CopyBlock
                text={`// Disable event tracking
FinvuManager.shared.setEventsEnabled(false)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />
            <Typography sx={{ mt: 2, mb: 3, color: "#555", fontStyle: 'italic' }}>
                <strong>Note:</strong> Disabling events will stop all event tracking, but listeners remain registered. Remove listeners separately if needed.
            </Typography>

            {/* Tracking Custom Events */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Tracking Custom Events
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                You can track custom events in your application. First, you need to register your custom events, then you can track them.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Registering Custom Events
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Before tracking custom events, you must register them using <code>FinvuManager.shared.registerCustomEvents()</code> or <code>FinvuEventTracker.shared.registerCustomEvents()</code>. This defines the event structure and category.
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.FinvuManager
import com.finvu.android.events.EventDefinition

// Register custom events with their definitions
val customEvents = mapOf(
    "CUSTOM_BUTTON_CLICKED" to EventDefinition("ui", count = 0),
    "CUSTOM_API_CALLED" to EventDefinition("api", count = 0),
    "USER_ACTION_COMPLETED" to EventDefinition("user_action", count = 0)
)

FinvuManager.shared.registerCustomEvents(customEvents)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                EventDefinition Parameters:
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>category</code> (String, required): The category of the event (e.g., "ui", "api", "user_action", "navigation", "business")</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>count</code> (Int, default: 0): Initial count (usually 0, will be incremented automatically when event is tracked)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>stage</code> (String?, optional): Optional stage identifier for workflow tracking</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fipId</code> (String?, optional): Optional FIP ID for FIP-specific events</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fips</code> (MutableList&lt;String&gt;, optional): Optional list of FIP IDs (for multi-FIP events)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fiTypes</code> (MutableList&lt;String&gt;, optional): Optional list of FI types (for multi-FI type events)</Typography></li>
            </Box>

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Method Signature:
            </Typography>
            <CopyBlock
                text={`// Using FinvuManager (recommended)
FinvuManager.shared.registerCustomEvents(events: Map<String, EventDefinition>)

// Or directly using FinvuEventTracker
FinvuEventTracker.shared.registerCustomEvents(events: Map<String, EventDefinition>)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Box sx={{ 
                bgcolor: 'rgba(11, 52, 82, 0.05)', 
                border: '1px solid rgba(11, 52, 82, 0.2)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                    📝 Important Notes
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Custom event names must be unique and cannot conflict with standard SDK event names</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Register custom events before tracking them (typically during app initialization)</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>The method is thread-safe and can be called from any thread</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>If you try to register an event name that already exists (standard or custom), it will be logged as an error and skipped</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Custom events will be included in event listeners just like standard SDK events</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>The <code>count</code> parameter in EventDefinition is automatically incremented each time the event is tracked</Typography></li>
                </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Basic Custom Event
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                After registering, you can track custom events:
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.events.FinvuEventTracker

// Track a custom event (must be registered first)
FinvuEventTracker.shared.track("CUSTOM_BUTTON_CLICKED")`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Custom Event with Parameters
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Track event with parameters:
            </Typography>
            <CopyBlock
                text={`// Track event with parameters
FinvuEventTracker.shared.track(
    "CUSTOM_EVENT_NAME",
    mapOf(
        "screen" to "Dashboard",
        "action" to "ViewAccounts"
    )
)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Parameters are automatically sanitized to remove any potential PII (Personally Identifiable Information). Only include safe metadata.
                </Typography>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example: Track User Actions
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.FinvuManager
import com.finvu.android.events.EventDefinition
import com.finvu.android.events.FinvuEventTracker

class DashboardActivity : AppCompatActivity() {
    
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        
        // Register custom events (do this once, e.g., in Application class or Activity onCreate)
        val customEvents = mapOf(
            "USER_ACTION_DISCOVER_ACCOUNTS" to EventDefinition("user_action", count = 0),
            "USER_ACTION_VIEW_CONSENTS" to EventDefinition("user_action", count = 0)
        )
        FinvuManager.shared.registerCustomEvents(customEvents)
    }
    
    private fun onDiscoverAccountsButtonClicked() {
        // Track custom event (must be registered first)
        FinvuEventTracker.shared.track(
            "USER_ACTION_DISCOVER_ACCOUNTS",
            mapOf(
                "source" to "dashboard_button",
                "timestamp" to System.currentTimeMillis()
            )
        )
        
        // Perform discovery
        discoverAccounts()
    }
    
    private fun onViewConsentsButtonClicked() {
        FinvuEventTracker.shared.track(
            "USER_ACTION_VIEW_CONSENTS",
            mapOf("screen" to "dashboard")
        )
        
        navigateToConsents()
    }
}`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Example: Registering and Tracking Custom Events
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.FinvuManager
import com.finvu.android.events.EventDefinition
import com.finvu.android.events.FinvuEventTracker

// In your Application class or initialization code
object EventSetup {
    fun registerCustomEvents() {
        val customEvents = mapOf(
            "BUTTON_CLICKED" to EventDefinition("ui", count = 0),
            "SCREEN_VIEWED" to EventDefinition("navigation", count = 0),
            "API_REQUEST" to EventDefinition("api", count = 0),
            "USER_ACTION" to EventDefinition("user_action", count = 0)
        )
        FinvuManager.shared.registerCustomEvents(customEvents)
    }
}

// Then track them anywhere in your app
class MyActivity : AppCompatActivity() {
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        
        // Register custom events (typically done once in Application)
        EventSetup.registerCustomEvents()
        
        // Now you can track custom events
        FinvuEventTracker.shared.track("BUTTON_CLICKED", mapOf("buttonId" to "login"))
        FinvuEventTracker.shared.track("SCREEN_VIEWED", mapOf("screen" to "Dashboard"))
    }
}`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Using Event Type Enum (Recommended)
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                The SDK provides the <code>FinvuEventType</code> enum for all standard events. Using the enum instead of string literals helps avoid typos and provides better IDE autocomplete support.
            </Typography>

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Available Enum Cases
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                The <code>FinvuEventType</code> enum includes all standard SDK events:
            </Typography>
            <CopyBlock
                text={`// WebSocket Events
FinvuEventType.WEBSOCKET_CONNECTED
FinvuEventType.WEBSOCKET_DISCONNECTED

// Authentication Events
FinvuEventType.LOGIN_INITIATED
FinvuEventType.LOGIN_OTP_GENERATED
FinvuEventType.LOGIN_OTP_VERIFIED
FinvuEventType.LOGIN_OTP_FAILED
// ... and more

// Discovery Events
FinvuEventType.DISCOVERY_INITIATED
FinvuEventType.ACCOUNTS_DISCOVERED
// ... and more

// Linking Events
FinvuEventType.LINKING_INITIATED
FinvuEventType.LINKING_SUCCESS
// ... and more

// Consent Events
FinvuEventType.CONSENT_APPROVED
FinvuEventType.CONSENT_DENIED
// ... and more`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Using Enum in Event Listeners
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                Use the enum when comparing event names in your listeners:
            </Typography>
            <CopyBlock
                text={`class MyEventListener : FinvuEventListener {
    override fun onEvent(event: FinvuEvent) {
        // Use enum instead of string literals
        when (event.eventName) {
            FinvuEventType.LOGIN_OTP_VERIFIED.eventName -> {
                println("Login successful")
            }
            FinvuEventType.CONSENT_APPROVED.eventName -> {
                println("Consent approved")
            }
            FinvuEventType.LINKING_SUCCESS.eventName -> {
                println("Account linked")
            }
        }
    }
}`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Using Enum for Tracking
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                When tracking standard SDK events, use the enum:
            </Typography>
            <CopyBlock
                text={`// Instead of string literals (error-prone)
FinvuEventTracker.shared.track("LOGIN_INITIATED")  // ❌ Typo risk

// Use enum (type-safe)
FinvuEventTracker.shared.track(FinvuEventType.LOGIN_INITIATED.eventName)  // ✅ Safe

// With parameters
FinvuEventTracker.shared.track(
    FinvuEventType.DISCOVERY_INITIATED.eventName,
    mapOf("fipId" to "fip123")
)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Using Enum with Aliases
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                You can also use the enum when registering aliases:
            </Typography>
            <CopyBlock
                text={`FinvuEventTracker.shared.registerAliases(
    mapOf(
        FinvuEventType.LOGIN_OTP_VERIFIED.eventName to "user_login_success",
        FinvuEventType.CONSENT_APPROVED.eventName to "consent_granted",
        FinvuEventType.LINKING_SUCCESS.eventName to "account_linked"
    )
)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Getting All Available Events
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                You can iterate over all available events using <code>values()</code>:
            </Typography>
            <CopyBlock
                text={`// Get all event names
val allEventNames = FinvuEventType.values().map { it.eventName }
println("Available events: $allEventNames")`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Event Aliases */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Aliases
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                Event aliases allow you to map standard SDK event names to custom names. This is useful when you want to integrate with analytics platforms that require specific event names.
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.events.FinvuEventTracker
import com.finvu.android.publicInterface.FinvuEventType

// Register aliases for SDK events
FinvuEventTracker.shared.registerAliases(
    mapOf(
        FinvuEventType.LOGIN_OTP_VERIFIED.eventName to "user_login_success",
        FinvuEventType.CONSENT_APPROVED.eventName to "consent_granted",
        FinvuEventType.LINKING_SUCCESS.eventName to "account_linked",
        FinvuEventType.ACCOUNTS_DISCOVERED.eventName to "accounts_found"
    )
)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Complete Examples */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Examples
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Example 1: SDK Journey Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Track events across the entire SDK journey (login → discovery → linking → consent):
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.FinvuManager
import com.finvu.android.publicInterface.FinvuEvent
import com.finvu.android.publicInterface.FinvuEventListener
import com.finvu.android.publicInterface.FinvuEventType

// Global event listener for SDK journey
class SDKJourneyListener : FinvuEventListener {
    override fun onEvent(event: FinvuEvent) {
        println("📊 SDK Journey: \${event.eventName}")
        
        // Track journey milestones using enum
        when (event.eventName) {
            FinvuEventType.LOGIN_INITIATED.eventName -> {
                println("   → Login journey started")
            }
            FinvuEventType.LOGIN_OTP_VERIFIED.eventName -> {
                println("   → User logged in successfully")
            }
            FinvuEventType.DISCOVERY_INITIATED.eventName -> {
                println("   → Account discovery started")
            }
            FinvuEventType.ACCOUNTS_DISCOVERED.eventName -> {
                println("   → Accounts found")
            }
            FinvuEventType.LINKING_INITIATED.eventName -> {
                println("   → Account linking started")
            }
            FinvuEventType.LINKING_SUCCESS.eventName -> {
                println("   → Account linked successfully")
            }
            FinvuEventType.CONSENT_APPROVED.eventName -> {
                println("   → Consent approved")
            }
        }
        
        // Send to analytics
        sendToAnalytics(event)
    }
    
    private fun sendToAnalytics(event: FinvuEvent) {
        // Your analytics integration
    }
}

// SDK Journey Manager
object SDKJourneyManager {
    private val eventListener = SDKJourneyListener()
    private var isTrackingEnabled = false
    
    fun startTracking() {
        if (isTrackingEnabled) return
        
        // Add listener
        FinvuManager.shared.addEventListener(eventListener)
        
        // Enable events
        FinvuManager.shared.setEventsEnabled(true)
        
        isTrackingEnabled = true
        println("✅ SDK Journey tracking started")
    }
    
    fun stopTracking() {
        if (!isTrackingEnabled) return
        
        // Disable events
        FinvuManager.shared.setEventsEnabled(false)
        
        // Remove listener
        FinvuManager.shared.removeEventListener(eventListener)
        
        isTrackingEnabled = false
        println("🛑 SDK Journey tracking stopped")
    }
}`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example 2: Analytics Integration
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Integrate with Analytics:
            </Typography>
            <CopyBlock
                text={`import com.finvu.android.publicInterface.FinvuEvent
import com.finvu.android.publicInterface.FinvuEventListener
import com.finvu.android.publicInterface.FinvuEventType

class AnalyticsListener : FinvuEventListener {
    override fun onEvent(event: FinvuEvent) {
        // Map SDK events to analytics events
        val analyticsEventName = mapToAnalyticsEvent(event.eventName)
        
        // Log event to analytics platform
        Analytics.track(analyticsEventName, event.params)
        
        // Also set user properties for important events using enum
        if (event.eventName == FinvuEventType.LOGIN_OTP_VERIFIED.eventName) {
            Analytics.setUserProperty("logged_in", "sdk_status")
        }
    }
    
    private fun mapToAnalyticsEvent(sdkEvent: String): String {
        // Map SDK event names to analytics-friendly names using enum
        return when (sdkEvent) {
            FinvuEventType.LOGIN_OTP_VERIFIED.eventName -> "sdk_login_success"
            FinvuEventType.CONSENT_APPROVED.eventName -> "sdk_consent_approved"
            FinvuEventType.LINKING_SUCCESS.eventName -> "sdk_account_linked"
            else -> "sdk_\${sdkEvent.lowercase()}"
        }
    }
}

// Setup
val analyticsListener = AnalyticsListener()
FinvuManager.shared.addEventListener(analyticsListener)
FinvuManager.shared.setEventsEnabled(true)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example 3: Debug Logging
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Create a debug listener for development:
            </Typography>
            <CopyBlock
                text={`import android.util.Log
import com.finvu.android.publicInterface.FinvuEvent
import com.finvu.android.publicInterface.FinvuEventListener

class DebugEventListener : FinvuEventListener {
    override fun onEvent(event: FinvuEvent) {
        if (BuildConfig.DEBUG) {
            val count = event.getParam<Int>("count") ?: 0
            Log.d("SDK", """
                🔍 [SDK Event]
                Name: \${event.eventName}
                Category: \${event.eventCategory}
                Timestamp: \${event.timestamp}
                Count: $count
                Params: \${event.params}
                SDK Version: \${event.aaSdkVersion}
            """.trimIndent())
        }
    }
}

// Add only in debug builds
if (BuildConfig.DEBUG) {
    val debugListener = DebugEventListener()
    FinvuManager.shared.addEventListener(debugListener)
}`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Event Data Structure */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Data Structure
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                Each event (<code>FinvuEvent</code>) contains the following properties:
            </Typography>
            <CopyBlock
                text={`data class FinvuEvent(
    val eventName: String,          // Event name (e.g., "LOGIN_OTP_VERIFIED")
    val eventCategory: String,      // Event category (e.g., "AUTHENTICATION")
    val timestamp: String,          // ISO 8601 timestamp
    val aaSdkVersion: String,       // SDK version
    val params: Map<String, Any?> = emptyMap()  // Event parameters (sanitized, no PII)
    // Note: count is stored in params["count"] (Int) - event occurrence count
)`}
                language="kotlin"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Best Practices */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Best Practices
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listeners when done: Always remove event listeners in <code>onDestroy()</code> or <code>onStop()</code> to prevent memory leaks.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use multiple listeners for separation of concerns: Create separate listeners for analytics, debugging, and business logic.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Sanitize custom event parameters: The SDK automatically sanitizes parameters, but avoid including PII in custom events.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Handle events on main thread: Event listeners are called on the main thread by default. For heavy processing, dispatch to background threads inside your listener implementation.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events for business metrics: Use custom events to track user actions and business-specific metrics.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use enum for type safety: Always use <code>FinvuEventType</code> enum instead of string literals to avoid typos and get IDE support.</Typography></li>
            </Box>

            {/* Troubleshooting */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Troubleshooting
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Events not being received?
            </Typography>
            <Box component="ol" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mb: 1 }}>
                        <strong>Check if events are enabled:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Make sure you called:
FinvuManager.shared.setEventsEnabled(true)`}
                        language="kotlin"
                        showLineNumbers={true}
                        theme={nord}
                    />
                </li>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mt: 2, mb: 1 }}>
                        <strong>Verify listener is added:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Ensure listener is added before events occur
FinvuManager.shared.addEventListener(eventListener)`}
                        language="kotlin"
                        showLineNumbers={true}
                        theme={nord}
                    />
                </li>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mt: 2, mb: 1 }}>
                        <strong>Check SDK connection:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Events may not fire if SDK is not connected
if (FinvuManager.shared.isConnected()) {
    // SDK is connected
}`}
                        language="kotlin"
                        showLineNumbers={true}
                        theme={nord}
                    />
                </li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Memory leaks?
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Always remove listeners when they're no longer needed</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listeners in <code>onDestroy()</code> or <code>onStop()</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Don't hold strong references to listeners in long-lived objects</Typography></li>
            </Box>

            {/* Summary */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Summary
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Enable tracking:</strong> <code>FinvuManager.shared.setEventsEnabled(true)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Add listener:</strong> <code>FinvuManager.shared.addEventListener(listener)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Remove listener:</strong> <code>FinvuManager.shared.removeEventListener(listener)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Register custom events:</strong> <code>FinvuManager.shared.registerCustomEvents(mapOf("EVENT_NAME" to EventDefinition(...)))</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Track custom events:</strong> <code>FinvuEventTracker.shared.track("EVENT_NAME", params = mapOf(...))</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Register event aliases:</strong> <code>FinvuEventTracker.shared.registerAliases(mapOf("EVENT_NAME" to "alias_name"))</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Available events:</strong> See <code>FinvuEventType</code> enum for all standard events</Typography></li>
            </Box>
            <Typography sx={{ mt: 2, mb: 3, color: "#555", fontStyle: 'italic' }}>
                For more examples, see the SDK repository examples.
            </Typography>
        </Box>
    );
};

// iOS Event Tracking Component
export const iOSEventTracking: React.FC = () => {
    return (
        <Box>
            {/* Overview */}
            <Typography variant="h6" sx={{ mt: 0, color: "#0b3452", fontWeight: "bold" }}>
                Overview
            </Typography>
            <Typography sx={{ mt: 2, mb: 3, color: "#555", lineHeight: 1.7 }}>
                The Finvu iOS SDK provides a comprehensive event tracking system that allows you to:
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Monitor SDK user journeys (login, discovery, linking, consent)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events in your application</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Integrate with analytics platforms</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Debug and troubleshoot user flows</Typography></li>
            </Box>
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3 
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Event tracking is <strong>disabled by default</strong>. You must enable it explicitly using <code>setEventsEnabled(true)</code>.
                </Typography>
            </Box>

            {/* Getting Started */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Getting Started
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Step 1: Enable Event Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Event tracking must be enabled before events will be tracked:
            </Typography>
            <CopyBlock
                text={`import FinvuSDK

// Enable event tracking
FinvuManager.shared.setEventsEnabled(true)`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Step 2: Create an Event Listener
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Create a class that conforms to the <code>FinvuEventListener</code> protocol:
            </Typography>
            <CopyBlock
                text={`import FinvuSDK

class MyEventListener: NSObject, FinvuEventListener {
    func onEvent(_ event: FinvuEvent) {
        print("📊 Event: \\(event.eventName)")
        print("   Category: \\(event.eventCategory)")
        print("   Timestamp: \\(event.timestamp)")
        
        // Access count from params
        if let count = event.params["count"] as? Int {
            print("   Count: \\(count)")
        }
        
        // Access event parameters
        if let params = event.params as? [String: Any] {
            print("   Params: \\(params)")
        }
        
        // Handle specific events using enum (recommended)
        switch event.eventName {
        case FinvuEventType.loginOtpVerified.eventName:
            print("   → User logged in successfully")
            // Send to analytics
            sendToAnalytics(event: event)
            
        case FinvuEventType.consentApproved.eventName:
            print("   → Consent approved")
            // Track conversion
            trackConversion(event: event)
            
        default:
            break
        }
    }
    
    private func sendToAnalytics(event: FinvuEvent) {
        // Integrate with your analytics platform
        // Example: Firebase, Mixpanel, Amplitude, etc.
    }
    
    private func trackConversion(event: FinvuEvent) {
        // Track business metrics
    }
}`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Step 3: Add the Event Listener
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Add your listener to receive events:
            </Typography>
            <CopyBlock
                text={`let eventListener = MyEventListener()
FinvuManager.shared.addEventListener(eventListener)`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            {/* Available Events */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Available Events
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                The SDK provides a comprehensive set of events organized by category. All events are defined in the <code>FinvuEventType</code> enum:
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                WebSocket Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>websocketConnected</code> - WebSocket connection established</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>websocketDisconnected</code> - WebSocket connection closed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Redirection Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>consentRequestValid</code> - Consent request is valid</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>consentRequestInvalid</code> - Consent request is invalid</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Authentication Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>loginInitiated</code> - Login process started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>loginOtpGenerated</code> - OTP generated for login</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>loginOtpFailed</code> - OTP generation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>loginOtpLocked</code> - OTP attempts locked</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>loginOtpVerified</code> - OTP verified successfully</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>loginOtpNotVerified</code> - OTP verification failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>loginFallbackInitiated</code> - Login fallback initiated</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Discovery Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>discoveryInitiated</code> - Account discovery started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>accountsDiscovered</code> - Accounts discovered successfully</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>discoveryFailed</code> - Account discovery failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>accountsNotDiscovered</code> - No accounts discovered</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Linking Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>linkingInitiated</code> - Account linking started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>linkingOtpGenerated</code> - OTP generated for linking</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>linkingOtpFailed</code> - Linking OTP generation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>linkingSuccess</code> - Account linking succeeded</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>linkingFailure</code> - Account linking failed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Consent Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>linkedAccountsSummary</code> - Linked accounts summary received</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>consentApproved</code> - Consent approved by user</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>consentDenied</code> - Consent denied by user</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>approveConsentFailed</code> - Consent approval failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>consentHandleFailed</code> - Consent handle operation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>getConsentStatusFailed</code> - Failed to get consent status</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Error Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>sessionError</code> - Session error occurred</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>sessionFailure</code> - Session failure occurred</Typography></li>
            </Box>

            {/* Event Parameters Reference */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Parameters Reference
            </Typography>
            <Typography sx={{ mt: 2, mb: 3, color: "#555" }}>
                All events have a <code>count</code> parameter (Int) in <code>params</code> that tracks how many times the event has occurred. Access it via <code>event.params["count"]</code> or <code>event.getParam("count", as: Int.self)</code>.
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                For <code>accountsDiscovered</code>, <code>linkingSuccess</code>, and <code>linkedAccountsSummary</code>, the <code>count</code> in params represents the number of accounts (discovered/linked), not the event occurrence count.
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                For <code>linkedAccountsSummary</code>, <code>fips</code> and <code>fiTypes</code> arrays are only included if they are populated (non-empty). The <code>fips</code> array contains unique FIP names (not FIP IDs) sorted alphabetically, and <code>fiTypes</code> contains unique FI types sorted alphabetically from the linked accounts.
            </Typography>
            <Typography sx={{ mb: 3, color: "#555" }}>
                All event parameters are automatically sanitized to remove any potential PII (Personally Identifiable Information). Additional parameters listed below are only included when relevant to the specific event.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                WebSocket Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>websocketConnected</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>websocketDisconnected</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Redirection Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>consentRequestValid</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>consentRequestInvalid</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Authentication Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>loginInitiated</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>loginOtpGenerated</code></TableCell>
                            <TableCell><code>count</code> (Int), <code>authType</code> (String, optional)</TableCell>
                            <TableCell>Event count + auth type used (e.g., "OTP", "SNA")</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>loginOtpFailed</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>loginOtpLocked</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>loginOtpVerified</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>loginOtpNotVerified</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>loginFallbackInitiated</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Discovery Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>discoveryInitiated</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>accountsDiscovered</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Number of accounts discovered (note: this overrides the event occurrence count in params)</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>discoveryFailed</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>accountsNotDiscovered</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Linking Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>linkingInitiated</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>linkingOtpGenerated</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>linkingOtpFailed</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>linkingSuccess</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Number of accounts linked (note: this overrides the event occurrence count in params)</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>linkingFailure</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Consent Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>linkedAccountsSummary</code></TableCell>
                            <TableCell><code>count</code> (Int), <code>fips</code> (Array&lt;String&gt;, optional), <code>fiTypes</code> (Array&lt;String&gt;, optional)</TableCell>
                            <TableCell>Number of linked accounts (note: this overrides the event occurrence count in params) + unique FIP names (not FIP IDs) and unique FI types from linked accounts. Should be tracked when linked accounts are fetched successfully.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>consentApproved</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>consentDenied</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>approveConsentFailed</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>consentHandleFailed</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>getConsentStatusFailed</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Error Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>sessionError</code></TableCell>
                            <TableCell><code>count</code> (Int), <code>error</code> (String, optional)</TableCell>
                            <TableCell>Event occurrence count + error message if available</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>sessionFailure</code></TableCell>
                            <TableCell><code>count</code> (Int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ 
                bgcolor: 'rgba(11, 52, 82, 0.05)', 
                border: '1px solid rgba(11, 52, 82, 0.2)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 3
            }}>
                <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                    📝 Note
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>To access parameters, use <code>event.params["key"]</code> or <code>event.getParam("key", as: Type.self)</code>.</Typography></li>
                </Box>
            </Box>

            {/* Adding Event Listeners */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Adding Event Listeners
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                You can add multiple event listeners. Each listener will receive all events:
            </Typography>
            <CopyBlock
                text={`// Analytics listener
class AnalyticsListener: NSObject, FinvuEventListener {
    func onEvent(_ event: FinvuEvent) {
        // Send to analytics platform
        Analytics.track(event.eventName, parameters: event.params)
    }
}

// Debug listener
class DebugListener: NSObject, FinvuEventListener {
    func onEvent(_ event: FinvuEvent) {
        // Log for debugging
        print("🔍 [DEBUG] \\(event.eventName) at \\(event.timestamp)")
    }
}

// Add both listeners
let analyticsListener = AnalyticsListener()
let debugListener = DebugListener()

FinvuManager.shared.addEventListener(analyticsListener)
FinvuManager.shared.addEventListener(debugListener)`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            {/* Removing Event Listeners */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Removing Event Listeners
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                To stop receiving events, remove the listener:
            </Typography>
            <CopyBlock
                text={`// Remove a specific listener
FinvuManager.shared.removeEventListener(eventListener)

// Example: Remove listener when view disappears
class MyViewController: UIViewController {
    private let eventListener = MyEventListener()
    
    override func viewDidLoad() {
        super.viewDidLoad()
        FinvuManager.shared.addEventListener(eventListener)
    }
    
    override func viewWillDisappear(_ animated: Bool) {
        super.viewWillDisappear(animated)
        // Remove listener to prevent memory leaks
        FinvuManager.shared.removeEventListener(eventListener)
    }
}`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Always remove listeners when they're no longer needed to prevent memory leaks.
                </Typography>
            </Box>
            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Disabling Event Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                To completely disable event tracking:
            </Typography>
            <CopyBlock
                text={`// Disable event tracking
FinvuManager.shared.setEventsEnabled(false)`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />
            <Typography sx={{ mt: 2, mb: 3, color: "#555", fontStyle: 'italic' }}>
                <strong>Note:</strong> Disabling events will stop all event tracking, but listeners remain registered. Remove listeners separately if needed.
            </Typography>

            {/* Tracking Custom Events */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Tracking Custom Events
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                You can track custom events in your application. First, you need to register your custom events, then you can track them.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Registering Custom Events
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Before tracking custom events, you must register them using <code>FinvuEventTracker.shared.registerCustomEvents()</code>. This defines the event structure and category.
            </Typography>
            <CopyBlock
                text={`import FinvuSDK

// Register custom events with their definitions
let customEvents: [String: EventDefinition] = [
    "CUSTOM_BUTTON_CLICKED": EventDefinition(category: "ui", count: 0),
    "CUSTOM_API_CALLED": EventDefinition(category: "api", count: 0),
    "USER_ACTION_COMPLETED": EventDefinition(category: "user_action", count: 0)
]

FinvuEventTracker.shared.registerCustomEvents(customEvents)`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                EventDefinition Parameters:
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>category</code> (String, required): The category of the event (e.g., "ui", "api", "user_action", "navigation", "business")</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>count</code> (Int, default: 0): Initial count (usually 0, will be incremented automatically when event is tracked)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>stage</code> (String?, optional): Optional stage identifier for workflow tracking</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fipId</code> (String?, optional): Optional FIP ID for FIP-specific events</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fips</code> ([String]?, optional): Optional array of FIP names (for multi-FIP events)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fiTypes</code> ([String]?, optional): Optional array of FI types (for multi-FI type events)</Typography></li>
            </Box>

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Method Signature:
            </Typography>
            <CopyBlock
                text={`FinvuEventTracker.shared.registerCustomEvents(_ events: [String: EventDefinition])`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Box sx={{ 
                bgcolor: 'rgba(11, 52, 82, 0.05)', 
                border: '1px solid rgba(11, 52, 82, 0.2)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                    📝 Important Notes
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Custom event names must be unique and cannot conflict with standard SDK event names</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Register custom events before tracking them (typically during app initialization)</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>The method is thread-safe and can be called from any thread</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>If you try to register an event name that already exists (standard or custom), it will be logged as an error and skipped</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Custom events will be included in event listeners just like standard SDK events</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>The <code>count</code> parameter in EventDefinition is automatically incremented each time the event is tracked</Typography></li>
                </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Basic Custom Event
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                After registering, you can track custom events:
            </Typography>
            <CopyBlock
                text={`import FinvuSDK

// Track a custom event (must be registered first)
FinvuEventTracker.shared.track("CUSTOM_BUTTON_CLICKED")`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Custom Event with Parameters
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Track event with parameters:
            </Typography>
            <CopyBlock
                text={`// Track event with parameters
FinvuEventTracker.shared.track(
    "CUSTOM_EVENT_NAME",
    params: [
        "screen": "Dashboard",
        "action": "ViewAccounts"
    ]
)`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Parameters are automatically sanitized to remove any potential PII (Personally Identifiable Information). Only include safe metadata.
                </Typography>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example: Track User Actions
            </Typography>
            <CopyBlock
                text={`import FinvuSDK

class DashboardViewController: UIViewController {
    
    override func viewDidLoad() {
        super.viewDidLoad()
        
        // Register custom events (do this once, e.g., in Application class or viewDidLoad)
        let customEvents: [String: EventDefinition] = [
            "USER_ACTION_DISCOVER_ACCOUNTS": EventDefinition(category: "user_action", count: 0),
            "USER_ACTION_VIEW_CONSENTS": EventDefinition(category: "user_action", count: 0)
        ]
        FinvuEventTracker.shared.registerCustomEvents(customEvents)
    }
    
    @IBAction func discoverAccountsButtonTapped(_ sender: UIButton) {
        // Track custom event (must be registered first)
        FinvuEventTracker.shared.track(
            "USER_ACTION_DISCOVER_ACCOUNTS",
            params: [
                "source": "dashboard_button",
                "timestamp": Date().timeIntervalSince1970
            ]
        )
        
        // Perform discovery
        discoverAccounts()
    }
    
    @IBAction func viewConsentsButtonTapped(_ sender: UIButton) {
        FinvuEventTracker.shared.track(
            "USER_ACTION_VIEW_CONSENTS",
            params: ["screen": "dashboard"]
        )
        
        navigateToConsents()
    }
}`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Example: Registering and Tracking Custom Events
            </Typography>
            <CopyBlock
                text={`import FinvuSDK

// In your AppDelegate or initialization code
class EventSetup {
    static func registerCustomEvents() {
        let customEvents: [String: EventDefinition] = [
            "BUTTON_CLICKED": EventDefinition(category: "ui", count: 0),
            "SCREEN_VIEWED": EventDefinition(category: "navigation", count: 0),
            "API_REQUEST": EventDefinition(category: "api", count: 0),
            "USER_ACTION": EventDefinition(category: "user_action", count: 0)
        ]
        FinvuEventTracker.shared.registerCustomEvents(customEvents)
    }
}

// Then track them anywhere in your app
class MyViewController: UIViewController {
    override func viewDidLoad() {
        super.viewDidLoad()
        
        // Register custom events (typically done once in AppDelegate)
        EventSetup.registerCustomEvents()
        
        // Now you can track custom events
        FinvuEventTracker.shared.track("BUTTON_CLICKED", params: ["buttonId": "login"])
        FinvuEventTracker.shared.track("SCREEN_VIEWED", params: ["screen": "Dashboard"])
    }
}`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Using Event Type Enum (Recommended)
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                The SDK provides the <code>FinvuEventType</code> enum for all standard events. Using the enum instead of string literals helps avoid typos and provides better IDE autocomplete support.
            </Typography>

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Available Enum Cases
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                The <code>FinvuEventType</code> enum includes all standard SDK events:
            </Typography>
            <CopyBlock
                text={`// WebSocket Events
FinvuEventType.websocketConnected
FinvuEventType.websocketDisconnected

// Authentication Events
FinvuEventType.loginInitiated
FinvuEventType.loginOtpGenerated
FinvuEventType.loginOtpVerified
FinvuEventType.loginOtpFailed
// ... and more

// Discovery Events
FinvuEventType.discoveryInitiated
FinvuEventType.accountsDiscovered
// ... and more

// Linking Events
FinvuEventType.linkingInitiated
FinvuEventType.linkingSuccess
// ... and more

// Consent Events
FinvuEventType.consentApproved
FinvuEventType.consentDenied
// ... and more`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Using Enum in Event Listeners
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                Use the enum when comparing event names in your listeners:
            </Typography>
            <CopyBlock
                text={`class MyEventListener: NSObject, FinvuEventListener {
    func onEvent(_ event: FinvuEvent) {
        // Use enum instead of string literals
        switch event.eventName {
        case FinvuEventType.loginOtpVerified.eventName:
            print("Login successful")
            
        case FinvuEventType.consentApproved.eventName:
            print("Consent approved")
            
        case FinvuEventType.linkingSuccess.eventName:
            print("Account linked")
            
        default:
            break
        }
    }
}`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Using Enum for Tracking
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                When tracking standard SDK events, use the enum:
            </Typography>
            <CopyBlock
                text={`// Instead of string literals (error-prone)
FinvuEventTracker.shared.track("LOGIN_INITIATED")  // ❌ Typo risk

// Use enum (type-safe)
FinvuEventTracker.shared.track(FinvuEventType.loginInitiated.eventName)  // ✅ Safe

// With parameters
FinvuEventTracker.shared.track(
    FinvuEventType.discoveryInitiated.eventName,
    params: ["fipId": "fip123"]
)`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Using Enum with Aliases
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                You can also use the enum when registering aliases:
            </Typography>
            <CopyBlock
                text={`FinvuEventTracker.shared.registerAliases([
    FinvuEventType.loginOtpVerified.eventName: "user_login_success",
    FinvuEventType.consentApproved.eventName: "consent_granted",
    FinvuEventType.linkingSuccess.eventName: "account_linked"
])`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Getting All Available Events
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                You can iterate over all available events using <code>CaseIterable</code>:
            </Typography>
            <CopyBlock
                text={`// Get all event names
let allEventNames = FinvuEventType.allCases.map { $0.eventName }
print("Available events: \\(allEventNames)")`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            {/* Event Aliases */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Aliases
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                Event aliases allow you to map standard SDK event names to custom names. This is useful when you want to integrate with analytics platforms that require specific event names.
            </Typography>
            <CopyBlock
                text={`import FinvuSDK

// Register aliases for SDK events
FinvuEventTracker.shared.registerAliases([
    FinvuEventType.loginOtpVerified.eventName: "user_login_success",
    FinvuEventType.consentApproved.eventName: "consent_granted",
    FinvuEventType.linkingSuccess.eventName: "account_linked",
    FinvuEventType.accountsDiscovered.eventName: "accounts_found"
])`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            {/* Complete Examples */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Examples
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Example 1: SDK Journey Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Track events across the entire SDK journey (login → discovery → linking → consent):
            </Typography>
            <CopyBlock
                text={`import SwiftUI
import FinvuSDK

// Global event listener for SDK journey
class SDKJourneyListener: NSObject, FinvuEventListener {
    func onEvent(_ event: FinvuEvent) {
        print("📊 SDK Journey: \\(event.eventName)")
        
        // Track journey milestones using enum
        switch event.eventName {
        case FinvuEventType.loginInitiated.eventName:
            print("   → Login journey started")
        case FinvuEventType.loginOtpVerified.eventName:
            print("   → User logged in successfully")
        case FinvuEventType.discoveryInitiated.eventName:
            print("   → Account discovery started")
        case FinvuEventType.accountsDiscovered.eventName:
            print("   → Accounts found")
        case FinvuEventType.linkingInitiated.eventName:
            print("   → Account linking started")
        case FinvuEventType.linkingSuccess.eventName:
            print("   → Account linked successfully")
        case FinvuEventType.consentApproved.eventName:
            print("   → Consent approved")
        default:
            break
        }
        
        // Send to analytics
        sendToAnalytics(event: event)
    }
    
    private func sendToAnalytics(event: FinvuEvent) {
        // Your analytics integration
    }
}

// SDK Journey Manager
class SDKJourneyManager {
    static let shared = SDKJourneyManager()
    private let eventListener = SDKJourneyListener()
    private var isTrackingEnabled = false
    
    func startTracking() {
        guard !isTrackingEnabled else { return }
        
        // Add listener
        FinvuManager.shared.addEventListener(eventListener)
        
        // Enable events
        FinvuManager.shared.setEventsEnabled(true)
        
        isTrackingEnabled = true
        print("✅ SDK Journey tracking started")
    }
    
    func stopTracking() {
        guard isTrackingEnabled else { return }
        
        // Disable events
        FinvuManager.shared.setEventsEnabled(false)
        
        // Remove listener
        FinvuManager.shared.removeEventListener(eventListener)
        
        isTrackingEnabled = false
        print("🛑 SDK Journey tracking stopped")
    }
}`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example 2: Analytics Integration
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Integrate with Analytics:
            </Typography>
            <CopyBlock
                text={`import FinvuSDK

class AnalyticsListener: NSObject, FinvuEventListener {
    func onEvent(_ event: FinvuEvent) {
        // Map SDK events to analytics events
        let analyticsEventName = mapToAnalyticsEvent(event.eventName)
        
        // Log event to analytics platform
        Analytics.track(analyticsEventName, parameters: event.params)
        
        // Also set user properties for important events using enum
        if event.eventName == FinvuEventType.loginOtpVerified.eventName {
            Analytics.setUserProperty("logged_in", forName: "sdk_status")
        }
    }
    
    private func mapToAnalyticsEvent(_ sdkEvent: String) -> String {
        // Map SDK event names to analytics-friendly names using enum
        switch sdkEvent {
        case FinvuEventType.loginOtpVerified.eventName:
            return "sdk_login_success"
        case FinvuEventType.consentApproved.eventName:
            return "sdk_consent_approved"
        case FinvuEventType.linkingSuccess.eventName:
            return "sdk_account_linked"
        default:
            return "sdk_\\(sdkEvent.lowercased())"
        }
    }
}

// Setup
let analyticsListener = AnalyticsListener()
FinvuManager.shared.addEventListener(analyticsListener)
FinvuManager.shared.setEventsEnabled(true)`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example 3: Debug Logging
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Create a debug listener for development:
            </Typography>
            <CopyBlock
                text={`class DebugEventListener: NSObject, FinvuEventListener {
    func onEvent(_ event: FinvuEvent) {
        #if DEBUG
        let count = event.params["count"] as? Int ?? 0
        print("""
        🔍 [SDK Event]
        Name: \\(event.eventName)
        Category: \\(event.eventCategory)
        Timestamp: \\(event.timestamp)
        Count: \\(count)
        Params: \\(event.params)
        SDK Version: \\(event.aaSdkVersion)
        """)
        #endif
    }
}

// Add only in debug builds
#if DEBUG
let debugListener = DebugEventListener()
FinvuManager.shared.addEventListener(debugListener)
#endif`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            {/* Event Data Structure */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Data Structure
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                Each event (<code>FinvuEvent</code>) contains the following properties:
            </Typography>
            <CopyBlock
                text={`@objc public class FinvuEvent: NSObject {
    @objc public let eventName: String          // Event name (e.g., "LOGIN_OTP_VERIFIED")
    @objc public let eventCategory: String      // Event category (e.g., "AUTHENTICATION")
    @objc public let timestamp: String          // ISO 8601 timestamp
    @objc public let aaSdkVersion: String      // SDK version
    @objc public let params: [String: Any]     // Event parameters (sanitized, no PII)
    // Note: count is stored in params["count"] (Int) - event occurrence count
}`}
                language="swift"
                showLineNumbers={true}
                theme={dracula}
            />

            {/* Best Practices */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Best Practices
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listeners when done: Always remove event listeners in <code>deinit</code> or <code>viewWillDisappear</code> to prevent memory leaks.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use multiple listeners for separation of concerns: Create separate listeners for analytics, debugging, and business logic.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Sanitize custom event parameters: The SDK automatically sanitizes parameters, but avoid including PII in custom events.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Handle events on main queue: Event listeners are called on the main queue. For heavy processing, dispatch to background queues inside your listener implementation.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events for business metrics: Use custom events to track user actions and business-specific metrics.</Typography></li>
            </Box>

            {/* Troubleshooting */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Troubleshooting
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Events not being received?
            </Typography>
            <Box component="ol" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mb: 1 }}>
                        <strong>Check if events are enabled:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Make sure you called:
FinvuManager.shared.setEventsEnabled(true)`}
                        language="swift"
                        showLineNumbers={true}
                        theme={dracula}
                    />
                </li>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mt: 2, mb: 1 }}>
                        <strong>Verify listener is added:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Ensure listener is added before events occur
FinvuManager.shared.addEventListener(eventListener)`}
                        language="swift"
                        showLineNumbers={true}
                        theme={dracula}
                    />
                </li>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mt: 2, mb: 1 }}>
                        <strong>Check SDK connection:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Events may not fire if SDK is not connected
if FinvuManager.shared.isConnected() {
    // SDK is connected
}`}
                        language="swift"
                        showLineNumbers={true}
                        theme={dracula}
                    />
                </li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Memory leaks?
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Always remove listeners when they're no longer needed</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use weak references if storing listeners in view controllers</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listeners in <code>deinit</code> or <code>viewWillDisappear</code></Typography></li>
            </Box>

            {/* Summary */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Summary
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Enable tracking:</strong> <code>FinvuManager.shared.setEventsEnabled(true)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Add listener:</strong> <code>FinvuManager.shared.addEventListener(listener)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Remove listener:</strong> <code>FinvuManager.shared.removeEventListener(listener)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Register custom events:</strong> <code>FinvuEventTracker.shared.registerCustomEvents(["EVENT_NAME": EventDefinition(...)])</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Track custom events:</strong> <code>FinvuEventTracker.shared.track("EVENT_NAME", params: [...])</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Register event aliases:</strong> <code>FinvuEventTracker.shared.registerAliases(["EVENT_NAME": "alias_name"])</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Available events:</strong> See <code>FinvuEventType</code> enum for all standard events</Typography></li>
            </Box>
            <Typography sx={{ mt: 2, mb: 3, color: "#555", fontStyle: 'italic' }}>
                For more examples, see <code>Example/SDKJourneyEventTracking.swift</code> in the SDK repository.
            </Typography>
        </Box>
    );
};

// Flutter Event Tracking Component
export const FlutterEventTracking: React.FC = () => {
    return (
        <Box>
            {/* Overview */}
            <Typography variant="h6" sx={{ mt: 0, color: "#0b3452", fontWeight: "bold" }}>
                Overview
            </Typography>
            <Typography sx={{ mt: 2, mb: 3, color: "#555", lineHeight: 1.7 }}>
                The Finvu Flutter SDK provides a comprehensive event tracking system that allows you to:
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Monitor SDK user journeys (login, discovery, linking, consent)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events in your application</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Integrate with analytics platforms</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Debug and troubleshoot user flows</Typography></li>
            </Box>
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3 
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Event tracking is <strong>disabled by default</strong>. You must enable it explicitly using <code>setEventsEnabled(true)</code>.
                </Typography>
            </Box>

            {/* Getting Started */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Getting Started
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Step 1: Enable Event Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Event tracking must be enabled before events will be tracked:
            </Typography>
            <CopyBlock
                text={`import 'package:finvu_flutter_sdk/finvu_manager.dart';

// Enable event tracking
final finvuManager = FinvuManager();
finvuManager.setEventsEnabled(true);`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Step 2: Create an Event Listener
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Create a class that implements the <code>FinvuEventListener</code> interface:
            </Typography>
            <CopyBlock
                text={`import 'package:finvu_flutter_sdk/finvu_event.dart';
import 'package:finvu_flutter_sdk/finvu_event_listener.dart';

class MyEventListener implements FinvuEventListener {
  @override
  void onEvent(FinvuEvent event) {
    print('📊 Event: \${event.eventName}');
    print('   Category: \${event.eventCategory}');
    print('   Timestamp: \${event.timestamp}');
    
    // Access count from params
    final count = event.getParam<int>('count');
    if (count != null) {
      print('   Count: $count');
    }
    
    // Access event parameters
    print('   Params: \${event.params}');
    
    // Handle specific events
    switch (event.eventName) {
      case 'LOGIN_OTP_VERIFIED':
        print('   → User logged in successfully');
        // Send to analytics
        sendToAnalytics(event);
        break;
        
      case 'CONSENT_APPROVED':
        print('   → Consent approved');
        // Track conversion
        trackConversion(event);
        break;
    }
  }
  
  void sendToAnalytics(FinvuEvent event) {
    // Integrate with your analytics platform
    // Example: Firebase, Mixpanel, Amplitude, etc.
  }
  
  void trackConversion(FinvuEvent event) {
    // Track business metrics
  }
}`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Step 3: Add the Event Listener
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Add your listener to receive events:
            </Typography>
            <CopyBlock
                text={`final eventListener = MyEventListener();
final finvuManager = FinvuManager();
finvuManager.addEventListener(eventListener);`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Complete Setup Example */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Setup Example: Application Initialization
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                For a complete setup in your app initialization:
            </Typography>
            <CopyBlock
                text={`import 'package:flutter/material.dart';
import 'package:finvu_flutter_sdk/finvu_manager.dart';
import 'package:finvu_flutter_sdk/finvu_event.dart';
import 'package:finvu_flutter_sdk/finvu_event_listener.dart';

void main() {
  runApp(MyApp());
}

class MyApp extends StatefulWidget {
  @override
  _MyAppState createState() => _MyAppState();
}

class _MyAppState extends State<MyApp> {
  final _finvuManager = FinvuManager();
  final _eventListener = _FinvuEventListener();
  
  @override
  void initState() {
    super.initState();
    
    // Add event listener
    _finvuManager.addEventListener(_eventListener);
    
    // Enable event tracking
    _finvuManager.setEventsEnabled(true);
    
    print('✅ Finvu event tracking enabled');
  }
  
  @override
  void dispose() {
    // Remove listener when app terminates
    _finvuManager.removeEventListener();
    super.dispose();
  }
  
  @override
  Widget build(BuildContext context) {
    return MaterialApp(
      title: 'Finvu Demo',
      home: HomePage(),
    );
  }
}

class _FinvuEventListener implements FinvuEventListener {
  @override
  void onEvent(FinvuEvent event) {
    print('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    print('Event: \${event.eventName}');
    print('Category: \${event.eventCategory}');
    print('Timestamp: \${event.timestamp}');
    
    event.params.forEach((key, value) {
      print('  $key: $value');
    });
    print('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }
}`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Available Events */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Available Events
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                The SDK provides a comprehensive set of events organized by category:
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                WebSocket Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>WEBSOCKET_CONNECTED</code> - WebSocket connection established</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>WEBSOCKET_DISCONNECTED</code> - WebSocket connection closed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Redirection Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_REQUEST_VALID</code> - Consent request is valid</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_REQUEST_INVALID</code> - Consent request is invalid</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Authentication Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_INITIATED</code> - Login process started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_GENERATED</code> - OTP generated for login</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_FAILED</code> - OTP generation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_LOCKED</code> - OTP attempts locked</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_VERIFIED</code> - OTP verified successfully</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_NOT_VERIFIED</code> - OTP verification failed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Discovery Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>DISCOVERY_INITIATED</code> - Account discovery started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>ACCOUNTS_DISCOVERED</code> - Accounts discovered successfully</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>DISCOVERY_FAILED</code> - Account discovery failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>ACCOUNTS_NOT_DISCOVERED</code> - No accounts discovered</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Linking Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_INITIATED</code> - Account linking started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_OTP_GENERATED</code> - OTP generated for linking</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_OTP_FAILED</code> - Linking OTP generation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_SUCCESS</code> - Account linking succeeded</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_FAILURE</code> - Account linking failed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Consent Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LINKED_ACCOUNTS_SUMMARY</code> - Linked accounts summary received</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_APPROVED</code> - Consent approved by user</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_DENIED</code> - Consent denied by user</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>APPROVE_CONSENT_FAILED</code> - Consent approval failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_HANDLE_FAILED</code> - Consent handle operation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>GET_CONSENT_STATUS_FAILED</code> - Failed to get consent status</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Error Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>SESSION_ERROR</code> - Session error occurred</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>SESSION_FAILURE</code> - Session failure occurred</Typography></li>
            </Box>

            {/* Event Parameters Reference */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Parameters Reference
            </Typography>
            <Typography sx={{ mt: 2, mb: 3, color: "#555" }}>
                All events have a <code>count</code> parameter (int) in <code>params</code> that tracks how many times the event has occurred. Access it via <code>event.params["count"]</code> or <code>event.getParam&lt;int&gt;("count")</code>.
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                For <code>ACCOUNTS_DISCOVERED</code>, <code>LINKING_SUCCESS</code>, and <code>LINKED_ACCOUNTS_SUMMARY</code>, the <code>count</code> in params represents the number of accounts (discovered/linked), not the event occurrence count.
            </Typography>
            <Typography sx={{ mb: 2, color: "#555" }}>
                For <code>LINKED_ACCOUNTS_SUMMARY</code>, <code>fips</code> and <code>fiTypes</code> lists are only included if they are populated (non-empty). The <code>fips</code> list contains unique FIP names (not FIP IDs) sorted alphabetically, and <code>fiTypes</code> contains unique FI types sorted alphabetically from the linked accounts.
            </Typography>
            <Typography sx={{ mb: 3, color: "#555" }}>
                All event parameters are automatically sanitized to remove any potential PII (Personally Identifiable Information). Additional parameters listed below are only included when relevant to the specific event.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                WebSocket Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>WEBSOCKET_CONNECTED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>WEBSOCKET_DISCONNECTED</code></TableCell>
                            <TableCell><code>count</code> (int), <code>code</code> (int, optional), <code>reason</code> (String, optional)</TableCell>
                            <TableCell>Event occurrence count + WebSocket close code and reason</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Redirection Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>CONSENT_REQUEST_VALID</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_REQUEST_INVALID</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Authentication Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LOGIN_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_GENERATED</code></TableCell>
                            <TableCell><code>count</code> (int), <code>authType</code> (String, optional)</TableCell>
                            <TableCell>Event count + auth type used (e.g., "OTP", "SNA")</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_FAILED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_LOCKED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_VERIFIED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_NOT_VERIFIED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Discovery Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>DISCOVERY_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>ACCOUNTS_DISCOVERED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Number of accounts discovered (note: this overrides the event occurrence count in params)</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>DISCOVERY_FAILED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>ACCOUNTS_NOT_DISCOVERED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Linking Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LINKING_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (int), <code>fipId</code> (String, optional)</TableCell>
                            <TableCell>Event occurrence count + FIP ID</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_OTP_GENERATED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_OTP_FAILED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_SUCCESS</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Number of accounts linked (note: this overrides the event occurrence count in params)</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_FAILURE</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Consent Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LINKED_ACCOUNTS_SUMMARY</code></TableCell>
                            <TableCell><code>count</code> (int), <code>fips</code> (List&lt;String&gt;, optional), <code>fiTypes</code> (List&lt;String&gt;, optional)</TableCell>
                            <TableCell>Number of linked accounts (note: this overrides the event occurrence count in params) + unique FIP names (not FIP IDs) and unique FI types from linked accounts. Should be tracked when linked accounts are fetched successfully.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_APPROVED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_DENIED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>APPROVE_CONSENT_FAILED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_HANDLE_FAILED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>GET_CONSENT_STATUS_FAILED</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Error Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>SESSION_ERROR</code></TableCell>
                            <TableCell><code>count</code> (int), <code>error</code> (String, optional)</TableCell>
                            <TableCell>Event occurrence count + error message if available</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>SESSION_FAILURE</code></TableCell>
                            <TableCell><code>count</code> (int)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ 
                bgcolor: 'rgba(11, 52, 82, 0.05)', 
                border: '1px solid rgba(11, 52, 82, 0.2)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 3
            }}>
                <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                    📝 Note
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>To access parameters, use <code>event.params["key"]</code> or <code>event.getParam&lt;Type&gt;("key")</code>.</Typography></li>
                </Box>
            </Box>

            {/* Adding Event Listeners */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Adding Event Listeners
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                You can add an event listener to receive all SDK events:
            </Typography>
            <CopyBlock
                text={`// Analytics listener
class AnalyticsListener implements FinvuEventListener {
  @override
  void onEvent(FinvuEvent event) {
    // Send to analytics platform
    Analytics.track(event.eventName, event.params);
  }
}

// Debug listener
class DebugListener implements FinvuEventListener {
  @override
  void onEvent(FinvuEvent event) {
    // Log for debugging
    debugPrint('🔍 [DEBUG] \${event.eventName} at \${event.timestamp}');
  }
}

// Add listener
final finvuManager = FinvuManager();
final analyticsListener = AnalyticsListener();
finvuManager.addEventListener(analyticsListener);
finvuManager.setEventsEnabled(true);`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Removing Event Listeners */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Removing Event Listeners
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                To stop receiving events, remove the listener:
            </Typography>
            <CopyBlock
                text={`// Remove listener
final finvuManager = FinvuManager();
finvuManager.removeEventListener();

// Example: Remove listener when widget is disposed
class MyWidget extends StatefulWidget {
  @override
  _MyWidgetState createState() => _MyWidgetState();
}

class _MyWidgetState extends State<MyWidget> {
  final _finvuManager = FinvuManager();
  final _eventListener = MyEventListener();
  
  @override
  void initState() {
    super.initState();
    _finvuManager.addEventListener(_eventListener);
  }
  
  @override
  void dispose() {
    // Remove listener to prevent memory leaks
    _finvuManager.removeEventListener();
    super.dispose();
  }
  
  @override
  Widget build(BuildContext context) {
    return Container();
  }
}`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Always remove listeners when they're no longer needed to prevent memory leaks.
                </Typography>
            </Box>
            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Disabling Event Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                To completely disable event tracking:
            </Typography>
            <CopyBlock
                text={`// Disable event tracking
final finvuManager = FinvuManager();
finvuManager.setEventsEnabled(false);`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Typography sx={{ mt: 2, mb: 3, color: "#555", fontStyle: 'italic' }}>
                <strong>Note:</strong> Disabling events will stop all event tracking, but listeners remain registered. Remove listeners separately if needed.
            </Typography>

            {/* Tracking Custom Events */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Tracking Custom Events
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                You can track custom events in your application. First, you need to register your custom events, then you can track them.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Registering Custom Events
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Before tracking custom events, you must register them using <code>registerCustomEvents()</code>. This defines the event structure and category.
            </Typography>
            <CopyBlock
                text={`import 'package:finvu_flutter_sdk/finvu_manager.dart';
import 'package:finvu_flutter_sdk/finvu_event_definition.dart';

// Register custom events with their definitions
final customEvents = {
  'CUSTOM_BUTTON_CLICKED': FinvuEventDefinition(
    category: 'ui',
  ),
  'CUSTOM_API_CALLED': FinvuEventDefinition(
    category: 'api',
  ),
  'USER_ACTION_COMPLETED': FinvuEventDefinition(
    category: 'user_action',
  ),
};

final finvuManager = FinvuManager();
finvuManager.registerCustomEvents(customEvents);`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                FinvuEventDefinition Parameters:
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>category</code> (String, required): The category of the event (e.g., "ui", "api", "user_action", "navigation", "business")</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>stage</code> (String?, optional): Optional stage identifier for workflow tracking</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fipId</code> (String?, optional): Optional FIP ID for FIP-specific events</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fips</code> (List&lt;String&gt;?, optional): Optional list of FIP names (for multi-FIP events)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><code>fiTypes</code> (List&lt;String&gt;?, optional): Optional list of FI types (for multi-FI type events)</Typography></li>
            </Box>

            <Typography sx={{ mt: 3, mb: 2, color: "#0b3452", fontWeight: "bold" }}>
                Method Signature:
            </Typography>
            <CopyBlock
                text={`void registerCustomEvents(Map<String, FinvuEventDefinition> events)`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Box sx={{ 
                bgcolor: 'rgba(11, 52, 82, 0.05)', 
                border: '1px solid rgba(11, 52, 82, 0.2)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                    📝 Important Notes
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Custom event names must be unique and cannot conflict with standard SDK event names</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Register custom events before tracking them (typically during app initialization)</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>The method is thread-safe and can be called from any thread</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>If you try to register an event name that already exists (standard or custom), it will be logged as an error and skipped</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Custom events will be included in event listeners just like standard SDK events</Typography></li>
                </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Basic Custom Event
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                After registering, you can track custom events:
            </Typography>
            <CopyBlock
                text={`import 'package:finvu_flutter_sdk/finvu_manager.dart';

final finvuManager = FinvuManager();

// Track a custom event (must be registered first)
finvuManager.track('CUSTOM_BUTTON_CLICKED');`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Custom Event with Parameters
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Track event with parameters:
            </Typography>
            <CopyBlock
                text={`// Track event with parameters
final finvuManager = FinvuManager();
finvuManager.track(
  'CUSTOM_EVENT_NAME',
  {
    'screen': 'Dashboard',
    'action': 'ViewAccounts',
  },
);`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Parameters are automatically sanitized to remove any potential PII (Personally Identifiable Information). Only include safe metadata.
                </Typography>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example: Track User Actions
            </Typography>
            <CopyBlock
                text={`import 'package:flutter/material.dart';
import 'package:finvu_flutter_sdk/finvu_manager.dart';
import 'package:finvu_flutter_sdk/finvu_event_definition.dart';

class DashboardPage extends StatefulWidget {
  @override
  _DashboardPageState createState() => _DashboardPageState();
}

class _DashboardPageState extends State<DashboardPage> {
  final _finvuManager = FinvuManager();
  
  @override
  void initState() {
    super.initState();
    
    // Register custom events (do this once, e.g., in initState)
    final customEvents = {
      'USER_ACTION_DISCOVER_ACCOUNTS': FinvuEventDefinition(
        category: 'user_action',
      ),
      'USER_ACTION_VIEW_CONSENTS': FinvuEventDefinition(
        category: 'user_action',
      ),
    };
    _finvuManager.registerCustomEvents(customEvents);
  }
  
  void _onDiscoverAccountsButtonClicked() {
    // Track custom event (must be registered first)
    _finvuManager.track(
      'USER_ACTION_DISCOVER_ACCOUNTS',
      {
        'source': 'dashboard_button',
        'timestamp': DateTime.now().millisecondsSinceEpoch,
      },
    );
    
    // Perform discovery
    discoverAccounts();
  }
  
  void _onViewConsentsButtonClicked() {
    _finvuManager.track(
      'USER_ACTION_VIEW_CONSENTS',
      {'screen': 'dashboard'},
    );
    
    navigateToConsents();
  }
  
  @override
  Widget build(BuildContext context) {
    return Scaffold(
      body: Column(
        children: [
          ElevatedButton(
            onPressed: _onDiscoverAccountsButtonClicked,
            child: Text('Discover Accounts'),
          ),
          ElevatedButton(
            onPressed: _onViewConsentsButtonClicked,
            child: Text('View Consents'),
          ),
        ],
      ),
    );
  }
  
  void discoverAccounts() {
    // Your discovery logic
  }
  
  void navigateToConsents() {
    // Your navigation logic
  }
}`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Example: Registering and Tracking Custom Events
            </Typography>
            <CopyBlock
                text={`import 'package:finvu_flutter_sdk/finvu_manager.dart';
import 'package:finvu_flutter_sdk/finvu_event_definition.dart';

// In your app initialization code
class EventSetup {
  static void registerCustomEvents() {
    final customEvents = {
      'BUTTON_CLICKED': FinvuEventDefinition(
        category: 'ui',
      ),
      'SCREEN_VIEWED': FinvuEventDefinition(
        category: 'navigation',
      ),
      'API_REQUEST': FinvuEventDefinition(
        category: 'api',
      ),
      'USER_ACTION': FinvuEventDefinition(
        category: 'user_action',
      ),
    };
    
    final finvuManager = FinvuManager();
    finvuManager.registerCustomEvents(customEvents);
  }
}

// Then track them anywhere in your app
class MyPage extends StatefulWidget {
  @override
  _MyPageState createState() => _MyPageState();
}

class _MyPageState extends State<MyPage> {
  final _finvuManager = FinvuManager();
  
  @override
  void initState() {
    super.initState();
    
    // Register custom events (typically done once in app initialization)
    EventSetup.registerCustomEvents();
    
    // Now you can track custom events
    _finvuManager.track('BUTTON_CLICKED', {'buttonId': 'login'});
    _finvuManager.track('SCREEN_VIEWED', {'screen': 'Dashboard'});
  }
  
  @override
  Widget build(BuildContext context) {
    return Container();
  }
}`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Event Aliases */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Aliases
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                Event aliases allow you to map standard SDK event names to custom names. This is useful when you want to integrate with analytics platforms that require specific event names.
            </Typography>
            <CopyBlock
                text={`import 'package:finvu_flutter_sdk/finvu_manager.dart';

// Register aliases for SDK events
final finvuManager = FinvuManager();
finvuManager.registerAliases({
  'LOGIN_OTP_VERIFIED': 'user_login_success',
  'CONSENT_APPROVED': 'consent_granted',
  'LINKING_SUCCESS': 'account_linked',
  'ACCOUNTS_DISCOVERED': 'accounts_found',
});`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Complete Examples */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Examples
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Example 1: SDK Journey Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Track events across the entire SDK journey (login → discovery → linking → consent):
            </Typography>
            <CopyBlock
                text={`import 'package:flutter/material.dart';
import 'package:finvu_flutter_sdk/finvu_manager.dart';
import 'package:finvu_flutter_sdk/finvu_event.dart';
import 'package:finvu_flutter_sdk/finvu_event_listener.dart';

// Global event listener for SDK journey
class SDKJourneyListener implements FinvuEventListener {
  @override
  void onEvent(FinvuEvent event) {
    print('📊 SDK Journey: \${event.eventName}');
    
    // Track journey milestones
    switch (event.eventName) {
      case 'LOGIN_INITIATED':
        print('   → Login journey started');
        break;
        
      case 'LOGIN_OTP_VERIFIED':
        print('   → User logged in successfully');
        break;
        
      case 'DISCOVERY_INITIATED':
        print('   → Account discovery started');
        break;
        
      case 'ACCOUNTS_DISCOVERED':
        final count = event.getParam<int>('count');
        print('   → Accounts found: $count');
        break;
        
      case 'LINKING_INITIATED':
        print('   → Account linking started');
        break;
        
      case 'LINKING_SUCCESS':
        final count = event.getParam<int>('count');
        print('   → Account linked successfully: $count');
        break;
        
      case 'CONSENT_APPROVED':
        print('   → Consent approved');
        break;
    }
    
    // Send to analytics
    sendToAnalytics(event);
  }
  
  void sendToAnalytics(FinvuEvent event) {
    // Your analytics integration
  }
}

// SDK Journey Manager
class SDKJourneyManager {
  static final SDKJourneyManager _instance = SDKJourneyManager._internal();
  factory SDKJourneyManager() => _instance;
  SDKJourneyManager._internal();
  
  final _finvuManager = FinvuManager();
  final _eventListener = SDKJourneyListener();
  bool _isTrackingEnabled = false;
  
  void startTracking() {
    if (_isTrackingEnabled) return;
    
    // Add listener
    _finvuManager.addEventListener(_eventListener);
    
    // Enable events
    _finvuManager.setEventsEnabled(true);
    
    _isTrackingEnabled = true;
    print('✅ SDK Journey tracking started');
  }
  
  void stopTracking() {
    if (!_isTrackingEnabled) return;
    
    // Disable events
    _finvuManager.setEventsEnabled(false);
    
    // Remove listener
    _finvuManager.removeEventListener();
    
    _isTrackingEnabled = false;
    print('🛑 SDK Journey tracking stopped');
  }
}`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example 2: Analytics Integration
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Integrate with Analytics:
            </Typography>
            <CopyBlock
                text={`import 'package:finvu_flutter_sdk/finvu_event.dart';
import 'package:finvu_flutter_sdk/finvu_event_listener.dart';

class AnalyticsListener implements FinvuEventListener {
  @override
  void onEvent(FinvuEvent event) {
    // Map SDK events to analytics events
    final analyticsEventName = _mapToAnalyticsEvent(event.eventName);
    
    // Log event to analytics platform
    Analytics.track(analyticsEventName, event.params);
    
    // Also set user properties for important events
    if (event.eventName == 'LOGIN_OTP_VERIFIED') {
      Analytics.setUserProperty('logged_in', 'sdk_status');
    }
  }
  
  String _mapToAnalyticsEvent(String sdkEvent) {
    // Map SDK event names to analytics-friendly names
    switch (sdkEvent) {
      case 'LOGIN_OTP_VERIFIED':
        return 'sdk_login_success';
      case 'CONSENT_APPROVED':
        return 'sdk_consent_approved';
      case 'LINKING_SUCCESS':
        return 'sdk_account_linked';
      default:
        return 'sdk_\${sdkEvent.toLowerCase()}';
    }
  }
}

// Setup
final finvuManager = FinvuManager();
final analyticsListener = AnalyticsListener();
finvuManager.addEventListener(analyticsListener);
finvuManager.setEventsEnabled(true);`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example 3: Debug Logging
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Create a debug listener for development:
            </Typography>
            <CopyBlock
                text={`import 'package:flutter/foundation.dart';
import 'package:finvu_flutter_sdk/finvu_event.dart';
import 'package:finvu_flutter_sdk/finvu_event_listener.dart';

class DebugEventListener implements FinvuEventListener {
  @override
  void onEvent(FinvuEvent event) {
    if (kDebugMode) {
      final count = event.getParam<int>('count') ?? 0;
      debugPrint('''
🔍 [SDK Event]
Name: \${event.eventName}
Category: \${event.eventCategory}
Timestamp: \${event.timestamp}
Count: $count
Params: \${event.params}
SDK Version: \${event.aaSdkVersion}
      ''');
    }
  }
}

// Add only in debug builds
if (kDebugMode) {
  final finvuManager = FinvuManager();
  final debugListener = DebugEventListener();
  finvuManager.addEventListener(debugListener);
}`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Event Data Structure */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Data Structure
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                Each event (<code>FinvuEvent</code>) contains the following properties:
            </Typography>
            <CopyBlock
                text={`class FinvuEvent {
  final String eventName;          // Event name (e.g., "LOGIN_OTP_VERIFIED")
  final String eventCategory;      // Event category (e.g., "AUTHENTICATION")
  final String timestamp;         // ISO 8601 timestamp
  final String aaSdkVersion;       // SDK version
  final Map<String, dynamic> params; // Event parameters (sanitized, no PII)
  
  // Helper method to get typed parameters
  T? getParam<T>(String key);
  
  // Note: count is stored in params["count"] (int) - event occurrence count
}`}
                language="dart"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Best Practices */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Best Practices
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listeners when done: Always remove event listeners in <code>dispose()</code> to prevent memory leaks.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use separate listeners for separation of concerns: Create separate listeners for analytics, debugging, and business logic.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Sanitize custom event parameters: The SDK automatically sanitizes parameters, but avoid including PII in custom events.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Handle events efficiently: Event listeners are called synchronously. For heavy processing, use isolates or async operations.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events for business metrics: Use custom events to track user actions and business-specific metrics.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use <code>getParam&lt;T&gt;()</code> for type-safe parameter access: This helps avoid runtime errors and provides better code clarity.</Typography></li>
            </Box>

            {/* Troubleshooting */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Troubleshooting
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Not receiving events?
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mt: 2, mb: 1 }}>
                        <strong>Check if events are enabled:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Ensure events are enabled
final finvuManager = FinvuManager();
finvuManager.setEventsEnabled(true);`}
                        language="dart"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </li>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mt: 2, mb: 1 }}>
                        <strong>Verify listener is added:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Ensure listener is added before events occur
final finvuManager = FinvuManager();
final eventListener = MyEventListener();
finvuManager.addEventListener(eventListener);`}
                        language="dart"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </li>
                <li>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mt: 2, mb: 1 }}>
                        <strong>Check SDK connection:</strong>
                    </Typography>
                    <CopyBlock
                        text={`// Events may not fire if SDK is not connected
final finvuManager = FinvuManager();
final isConnected = await finvuManager.isConnected();
if (isConnected) {
  // SDK is connected
}`}
                        language="dart"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Memory leaks?
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Always remove listeners when they're no longer needed</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listeners in <code>dispose()</code> method of StatefulWidget</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use weak references if storing listeners in global state</Typography></li>
            </Box>

            {/* Summary */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Summary
            </Typography>
            <Box component="ul" sx={{ mt: 2, mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Enable tracking:</strong> <code>finvuManager.setEventsEnabled(true)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Add listener:</strong> <code>finvuManager.addEventListener(listener)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Remove listener:</strong> <code>finvuManager.removeEventListener()</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Register custom events:</strong> <code>{`finvuManager.registerCustomEvents({'EVENT_NAME': FinvuEventDefinition(...)})`}</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Track custom events:</strong> <code>{`finvuManager.track('EVENT_NAME', {'param': 'value'})`}</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Register event aliases:</strong> <code>{`finvuManager.registerAliases({'EVENT_NAME': 'alias_name'})`}</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}><strong>Available events:</strong> See event list above for all standard events</Typography></li>
            </Box>
        </Box>
    );
};

// React Native Event Tracking Component
export const ReactNativeEventTracking: React.FC = () => {
    return (
        <Box>
            {/* Overview */}
            <Typography variant="h6" sx={{ mt: 0, color: "#0b3452", fontWeight: "bold" }}>
                Overview
            </Typography>
            <Typography sx={{ mt: 2, mb: 3, color: "#555", lineHeight: 1.7 }}>
                The Finvu React Native SDK provides a comprehensive event tracking system that allows you to:
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Monitor SDK user journeys (login, discovery, linking, consent)</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events in your application</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Integrate with analytics platforms</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Debug and troubleshoot user flows</Typography></li>
            </Box>
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3 
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Event tracking is <strong>disabled by default</strong>. You must enable it explicitly using <code>setEventsEnabled(true)</code>.
                </Typography>
            </Box>

            {/* Getting Started */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Getting Started
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Step 1: Enable Event Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Event tracking must be enabled before events will be tracked:
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

// Enable event tracking
Finvu.setEventsEnabled(true);`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Step 2: Create an Event Listener
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Create a function that handles events:
            </Typography>
            <CopyBlock
                text={`import Finvu, { FinvuEvent } from '@cookiejar-technologies/finvu-react-native-sdk';

const handleEvent = (event: FinvuEvent) => {
  console.log('📊 Event:', event.eventName);
  console.log('   Category:', event.eventCategory);
  console.log('   Timestamp:', event.timestamp);
  
  // Access count from params
  const count = event.params?.count;
  if (count !== undefined) {
    console.log('   Count:', count);
  }
  
  // Access event parameters
  console.log('   Params:', event.params);
  
  // Handle specific events
  switch (event.eventName) {
    case 'LOGIN_OTP_VERIFIED':
      console.log('   → User logged in successfully');
      // Send to analytics
      sendToAnalytics(event);
      break;
      
    case 'CONSENT_APPROVED':
      console.log('   → Consent approved');
      // Track conversion
      trackConversion(event);
      break;
  }
};

const sendToAnalytics = (event: FinvuEvent) => {
  // Integrate with your analytics platform
  // Example: Firebase, Mixpanel, Amplitude, etc.
};

const trackConversion = (event: FinvuEvent) => {
  // Track business metrics
};`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Step 3: Add the Event Listener
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Add your listener to receive events:
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

const eventListener = handleEvent;
Finvu.addEventListener(eventListener);`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Complete Setup Example */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Setup Example: Application Initialization
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                For a complete setup in your app initialization:
            </Typography>
            <CopyBlock
                text={`import React, { useEffect } from 'react';
import { View, Text } from 'react-native';
import Finvu, { FinvuEvent } from '@cookiejar-technologies/finvu-react-native-sdk';

const App = () => {
  useEffect(() => {
    // Add event listener
    const eventListener = (event: FinvuEvent) => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('Event:', event.eventName);
      console.log('Category:', event.eventCategory);
      console.log('Timestamp:', event.timestamp);
      
      if (event.params) {
        Object.keys(event.params).forEach((key) => {
          console.log(\`  \${key}:\`, event.params[key]);
        });
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    };
    
    Finvu.addEventListener(eventListener);
    
    // Enable event tracking
    Finvu.setEventsEnabled(true);
    
    console.log('✅ Finvu event tracking enabled');
    
    // Cleanup function
    return () => {
      Finvu.removeEventListener();
    };
  }, []);
  
  return (
    <View>
      <Text>Finvu Demo</Text>
    </View>
  );
};

export default App;`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Available Events */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Available Events
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                The SDK provides a comprehensive set of events organized by category:
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                WebSocket Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>WEBSOCKET_CONNECTED</code> - WebSocket connection established</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>WEBSOCKET_DISCONNECTED</code> - WebSocket connection closed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Redirection Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_REQUEST_VALID</code> - Consent request is valid</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_REQUEST_INVALID</code> - Consent request is invalid</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Authentication Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_INITIATED</code> - Login process started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_GENERATED</code> - OTP generated for login</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_FAILED</code> - OTP generation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_LOCKED</code> - OTP attempts locked</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_VERIFIED</code> - OTP verified successfully</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LOGIN_OTP_NOT_VERIFIED</code> - OTP verification failed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Discovery Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>DISCOVERY_INITIATED</code> - Account discovery started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>ACCOUNTS_DISCOVERED</code> - Accounts discovered successfully</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>DISCOVERY_FAILED</code> - Account discovery failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>ACCOUNTS_NOT_DISCOVERED</code> - No accounts discovered</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Linking Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_INITIATED</code> - Account linking started</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_OTP_GENERATED</code> - OTP generated for linking</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_OTP_FAILED</code> - Linking OTP generation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_SUCCESS</code> - Account linking succeeded</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>LINKING_FAILURE</code> - Account linking failed</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Consent Events
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>LINKED_ACCOUNTS_SUMMARY</code> - Linked accounts summary received</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_APPROVED</code> - Consent approved by user</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_DENIED</code> - Consent denied by user</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>APPROVE_CONSENT_FAILED</code> - Consent approval failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>CONSENT_HANDLE_FAILED</code> - Consent handle operation failed</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>GET_CONSENT_STATUS_FAILED</code> - Failed to get consent status</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Error Events
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>SESSION_ERROR</code> - Session error occurred</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>SESSION_FAILURE</code> - Session failure occurred</Typography></li>
            </Box>

            {/* Event Parameters Reference */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Parameters Reference
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                All events have a <code>count</code> parameter (number) in params that tracks how many times the event has occurred. Access it via <code>event.params?.count</code> or <code>event.params?.["count"]</code>.
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                For <code>ACCOUNTS_DISCOVERED</code>, <code>LINKING_SUCCESS</code>, and <code>LINKED_ACCOUNTS_SUMMARY</code>, the count in params represents the number of accounts (discovered/linked), not the event occurrence count.
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                For <code>LINKED_ACCOUNTS_SUMMARY</code>, <code>fips</code> and <code>fiTypes</code> arrays are only included if they are populated (non-empty). The <code>fips</code> array contains unique FIP names (not FIP IDs) sorted alphabetically, and <code>fiTypes</code> contains unique FI types sorted alphabetically from the linked accounts.
            </Typography>
            <Typography sx={{ mt: 2, mb: 3, color: "#555" }}>
                All event parameters are automatically sanitized to remove any potential PII (Personally Identifiable Information). Additional parameters listed below are only included when relevant to the specific event.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                WebSocket Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>WEBSOCKET_CONNECTED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>WEBSOCKET_DISCONNECTED</code></TableCell>
                            <TableCell><code>count</code> (number), <code>code</code> (number, optional), <code>reason</code> (string, optional)</TableCell>
                            <TableCell>Event occurrence count + WebSocket close code and reason</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Redirection Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>CONSENT_REQUEST_VALID</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_REQUEST_INVALID</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Authentication Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LOGIN_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_GENERATED</code></TableCell>
                            <TableCell><code>count</code> (number), <code>authType</code> (string, optional)</TableCell>
                            <TableCell>Event count + auth type used (e.g., "OTP", "SNA")</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_FAILED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_LOCKED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_VERIFIED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LOGIN_OTP_NOT_VERIFIED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Discovery Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>DISCOVERY_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>ACCOUNTS_DISCOVERED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Number of accounts discovered (note: this overrides the event occurrence count in params)</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>DISCOVERY_FAILED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>ACCOUNTS_NOT_DISCOVERED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Linking Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LINKING_INITIATED</code></TableCell>
                            <TableCell><code>count</code> (number), <code>fipId</code> (string, optional)</TableCell>
                            <TableCell>Event occurrence count + FIP ID</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_OTP_GENERATED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_OTP_FAILED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_SUCCESS</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Number of accounts linked (note: this overrides the event occurrence count in params)</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>LINKING_FAILURE</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Consent Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>LINKED_ACCOUNTS_SUMMARY</code></TableCell>
                            <TableCell><code>count</code> (number), <code>fips</code> (string[], optional), <code>fiTypes</code> (string[], optional)</TableCell>
                            <TableCell>Number of linked accounts (note: this overrides the event occurrence count in params) + unique FIP names (not FIP IDs) and unique FI types from linked accounts. Should be tracked when linked accounts are fetched successfully.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_APPROVED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_DENIED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>APPROVE_CONSENT_FAILED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>CONSENT_HANDLE_FAILED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>GET_CONSENT_STATUS_FAILED</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Error Events
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, mt: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Event Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Parameters (in params)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell><code>SESSION_ERROR</code></TableCell>
                            <TableCell><code>count</code> (number), <code>error</code> (string, optional)</TableCell>
                            <TableCell>Event occurrence count + error message if available</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><code>SESSION_FAILURE</code></TableCell>
                            <TableCell><code>count</code> (number)</TableCell>
                            <TableCell>Event occurrence count</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ 
                bgcolor: 'rgba(11, 52, 82, 0.05)', 
                border: '1px solid rgba(11, 52, 82, 0.2)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 3
            }}>
                <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                    📝 Note
                </Typography>
                <Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>
                    To access parameters, use <code>event.params?.["key"]</code> or <code>event.params?.key</code>.
                </Typography>
            </Box>

            {/* Adding Event Listeners */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Adding Event Listeners
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                You can add an event listener to receive all SDK events:
            </Typography>
            <CopyBlock
                text={`import Finvu, { FinvuEvent } from '@cookiejar-technologies/finvu-react-native-sdk';

// Analytics listener
const analyticsListener = (event: FinvuEvent) => {
  // Send to analytics platform
  Analytics.track(event.eventName, event.params);
};

// Debug listener
const debugListener = (event: FinvuEvent) => {
  // Log for debugging
  console.log('🔍 [DEBUG]', event.eventName, 'at', event.timestamp);
};

// Add listener
Finvu.addEventListener(analyticsListener);
Finvu.setEventsEnabled(true);`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Removing Event Listeners */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Removing Event Listeners
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                To stop receiving events, remove the listener:
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

// Remove listener
Finvu.removeEventListener();

// Example: Remove listener when component is unmounted
import React, { useEffect } from 'react';
import Finvu, { FinvuEvent } from '@cookiejar-technologies/finvu-react-native-sdk';

const MyComponent = () => {
  useEffect(() => {
    const eventListener = (event: FinvuEvent) => {
      // Handle events
    };
    
    Finvu.addEventListener(eventListener);
    
    // Remove listener to prevent memory leaks
    return () => {
      Finvu.removeEventListener();
    };
  }, []);
  
  return null;
};`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Always remove listeners when they're no longer needed to prevent memory leaks.
                </Typography>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Disabling Event Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                To completely disable event tracking:
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

// Disable event tracking
Finvu.setEventsEnabled(false);`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />
            <Typography sx={{ mt: 2, mb: 3, color: "#555", fontStyle: 'italic' }}>
                <strong>Note:</strong> Disabling events will stop all event tracking, but listeners remain registered. Remove listeners separately if needed.
            </Typography>

            {/* Tracking Custom Events */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Tracking Custom Events
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                You can track custom events in your application. First, you need to register your custom events, then you can track them.
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Registering Custom Events
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Before tracking custom events, you must register them using <code>registerCustomEvents()</code>. This defines the event structure and category.
            </Typography>
            <CopyBlock
                text={`import Finvu, { FinvuEventDefinition } from '@cookiejar-technologies/finvu-react-native-sdk';

// Register custom events with their definitions
const customEvents: Record<string, FinvuEventDefinition> = {
  'CUSTOM_BUTTON_CLICKED': {
    category: 'ui',
  },
  'CUSTOM_API_CALLED': {
    category: 'api',
  },
  'USER_ACTION_COMPLETED': {
    category: 'user_action',
  },
};

Finvu.registerCustomEvents(customEvents);`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                FinvuEventDefinition Parameters:
            </Typography>
            <Box component="ul" sx={{ mb: 2, pl: 3 }}>
                <li><Typography sx={{ color: "#555" }}><code>category</code> (string, required): The category of the event (e.g., "ui", "api", "user_action", "navigation", "business")</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>stage</code> (string?, optional): Optional stage identifier for workflow tracking</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>fipId</code> (string?, optional): Optional FIP ID for FIP-specific events</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>fips</code> (string[]?, optional): Optional array of FIP names (for multi-FIP events)</Typography></li>
                <li><Typography sx={{ color: "#555" }}><code>fiTypes</code> (string[]?, optional): Optional array of FI types (for multi-FI type events)</Typography></li>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Method Signature:
            </Typography>
            <CopyBlock
                text={`registerCustomEvents(events: Record<string, FinvuEventDefinition>): void`}
                language="typescript"
                showLineNumbers={false}
                theme={nord}
            />

            <Box sx={{ 
                bgcolor: 'rgba(11, 52, 82, 0.05)', 
                border: '1px solid rgba(11, 52, 82, 0.2)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 3
            }}>
                <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                    📝 Important Notes
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Custom event names must be unique and cannot conflict with standard SDK event names</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Register custom events before tracking them (typically during app initialization)</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>The method is thread-safe and can be called from any thread</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>If you try to register an event name that already exists (standard or custom), it will be logged as an error and skipped</Typography></li>
                    <li><Typography sx={{ color: "#555", fontSize: '0.9rem', lineHeight: 1.7 }}>Custom events will be included in event listeners just like standard SDK events</Typography></li>
                </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Basic Custom Event
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                After registering, you can track custom events:
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

// Track a custom event (must be registered first)
Finvu.track('CUSTOM_BUTTON_CLICKED');`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Custom Event with Parameters
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Track event with parameters:
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

// Track event with parameters
Finvu.track('CUSTOM_EVENT_NAME', {
  screen: 'Dashboard',
  action: 'ViewAccounts',
});`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />
            <Box sx={{ 
                bgcolor: 'rgba(255, 193, 7, 0.1)', 
                border: '1px solid rgba(255, 193, 7, 0.3)', 
                borderRadius: '4px', 
                p: 2, 
                mb: 3,
                mt: 2
            }}>
                <Typography sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
                    ⚠️ Important
                </Typography>
                <Typography sx={{ color: '#856404' }}>
                    Parameters are automatically sanitized to remove any potential PII (Personally Identifiable Information). Only include safe metadata.
                </Typography>
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example: Track User Actions
            </Typography>
            <CopyBlock
                text={`import React, { useEffect } from 'react';
import { View, Button } from 'react-native';
import Finvu, { FinvuEventDefinition } from '@cookiejar-technologies/finvu-react-native-sdk';

const DashboardPage = () => {
  useEffect(() => {
    // Register custom events (do this once, e.g., in useEffect)
    const customEvents: Record<string, FinvuEventDefinition> = {
      'USER_ACTION_DISCOVER_ACCOUNTS': {
        category: 'user_action',
      },
      'USER_ACTION_VIEW_CONSENTS': {
        category: 'user_action',
      },
    };
    Finvu.registerCustomEvents(customEvents);
  }, []);
  
  const onDiscoverAccountsButtonClicked = () => {
    // Track custom event (must be registered first)
    Finvu.track('USER_ACTION_DISCOVER_ACCOUNTS', {
      source: 'dashboard_button',
      timestamp: Date.now(),
    });
    
    // Perform discovery
    discoverAccounts();
  };
  
  const onViewConsentsButtonClicked = () => {
    Finvu.track('USER_ACTION_VIEW_CONSENTS', {
      screen: 'dashboard',
    });
    
    navigateToConsents();
  };
  
  return (
    <View>
      <Button
        title="Discover Accounts"
        onPress={onDiscoverAccountsButtonClicked}
      />
      <Button
        title="View Consents"
        onPress={onViewConsentsButtonClicked}
      />
    </View>
  );
};

const discoverAccounts = () => {
  // Your discovery logic
};

const navigateToConsents = () => {
  // Your navigation logic
};

export default DashboardPage;`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Example: Registering and Tracking Custom Events
            </Typography>
            <CopyBlock
                text={`import Finvu, { FinvuEventDefinition } from '@cookiejar-technologies/finvu-react-native-sdk';

// In your app initialization code
export const registerCustomEvents = () => {
  const customEvents: Record<string, FinvuEventDefinition> = {
    'BUTTON_CLICKED': {
      category: 'ui',
    },
    'SCREEN_VIEWED': {
      category: 'navigation',
    },
    'API_REQUEST': {
      category: 'api',
    },
    'USER_ACTION': {
      category: 'user_action',
    },
  };
  
  Finvu.registerCustomEvents(customEvents);
};

// Then track them anywhere in your app
import React, { useEffect } from 'react';
import { registerCustomEvents } from './eventSetup';
import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

const MyPage = () => {
  useEffect(() => {
    // Register custom events (typically done once in app initialization)
    registerCustomEvents();
    
    // Now you can track custom events
    Finvu.track('BUTTON_CLICKED', { buttonId: 'login' });
    Finvu.track('SCREEN_VIEWED', { screen: 'Dashboard' });
  }, []);
  
  return null;
};`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Event Aliases */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Aliases
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                Event aliases allow you to map standard SDK event names to custom names. This is useful when you want to integrate with analytics platforms that require specific event names.
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

// Register aliases for SDK events
Finvu.registerAliases({
  'LOGIN_OTP_VERIFIED': 'user_login_success',
  'CONSENT_APPROVED': 'consent_granted',
  'LINKING_SUCCESS': 'account_linked',
  'ACCOUNTS_DISCOVERED': 'accounts_found',
});`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Complete Examples */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Complete Examples
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Example 1: SDK Journey Tracking
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Track events across the entire SDK journey (login → discovery → linking → consent):
            </Typography>
            <CopyBlock
                text={`import React, { useEffect } from 'react';
import Finvu, { FinvuEvent } from '@cookiejar-technologies/finvu-react-native-sdk';

// Global event listener for SDK journey
const sdkJourneyListener = (event: FinvuEvent) => {
  console.log('📊 SDK Journey:', event.eventName);
  
  // Track journey milestones
  switch (event.eventName) {
    case 'LOGIN_INITIATED':
      console.log('   → Login journey started');
      break;
      
    case 'LOGIN_OTP_VERIFIED':
      console.log('   → User logged in successfully');
      break;
      
    case 'DISCOVERY_INITIATED':
      console.log('   → Account discovery started');
      break;
      
    case 'ACCOUNTS_DISCOVERED':
      const count = event.params?.count;
      console.log('   → Accounts found:', count);
      break;
      
    case 'LINKING_INITIATED':
      console.log('   → Account linking started');
      break;
      
    case 'LINKING_SUCCESS':
      const linkedCount = event.params?.count;
      console.log('   → Account linked successfully:', linkedCount);
      break;
      
    case 'CONSENT_APPROVED':
      console.log('   → Consent approved');
      break;
  }
  
  // Send to analytics
  sendToAnalytics(event);
};

const sendToAnalytics = (event: FinvuEvent) => {
  // Your analytics integration
};

// SDK Journey Manager
class SDKJourneyManager {
  private static instance: SDKJourneyManager;
  private isTrackingEnabled = false;
  
  static getInstance(): SDKJourneyManager {
    if (!SDKJourneyManager.instance) {
      SDKJourneyManager.instance = new SDKJourneyManager();
    }
    return SDKJourneyManager.instance;
  }
  
  startTracking() {
    if (this.isTrackingEnabled) return;
    
    // Add listener
    Finvu.addEventListener(sdkJourneyListener);
    
    // Enable events
    Finvu.setEventsEnabled(true);
    
    this.isTrackingEnabled = true;
    console.log('✅ SDK Journey tracking started');
  }
  
  stopTracking() {
    if (!this.isTrackingEnabled) return;
    
    // Disable events
    Finvu.setEventsEnabled(false);
    
    // Remove listener
    Finvu.removeEventListener();
    
    this.isTrackingEnabled = false;
    console.log('🛑 SDK Journey tracking stopped');
  }
}

// Usage in App component
const App = () => {
  useEffect(() => {
    const journeyManager = SDKJourneyManager.getInstance();
    journeyManager.startTracking();
    
    return () => {
      journeyManager.stopTracking();
    };
  }, []);
  
  return null;
};`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example 2: Analytics Integration
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Integrate with Analytics:
            </Typography>
            <CopyBlock
                text={`import Finvu, { FinvuEvent } from '@cookiejar-technologies/finvu-react-native-sdk';
// import Analytics from 'your-analytics-library';

const analyticsListener = (event: FinvuEvent) => {
  // Map SDK events to analytics events
  const analyticsEventName = mapToAnalyticsEvent(event.eventName);
  
  // Log event to analytics platform
  // Analytics.track(analyticsEventName, event.params);
  
  // Also set user properties for important events
  if (event.eventName === 'LOGIN_OTP_VERIFIED') {
    // Analytics.setUserProperty('logged_in', 'sdk_status');
  }
};

const mapToAnalyticsEvent = (sdkEvent: string): string => {
  // Map SDK event names to analytics-friendly names
  switch (sdkEvent) {
    case 'LOGIN_OTP_VERIFIED':
      return 'sdk_login_success';
    case 'CONSENT_APPROVED':
      return 'sdk_consent_approved';
    case 'LINKING_SUCCESS':
      return 'sdk_account_linked';
    default:
      return \`sdk_\${sdkEvent.toLowerCase()}\`;
  }
};

// Setup
Finvu.addEventListener(analyticsListener);
Finvu.setEventsEnabled(true);`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Example 3: Debug Logging
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Create a debug listener for development:
            </Typography>
            <CopyBlock
                text={`import Finvu, { FinvuEvent } from '@cookiejar-technologies/finvu-react-native-sdk';
import { __DEV__ } from 'react-native';

const debugEventListener = (event: FinvuEvent) => {
  if (__DEV__) {
    const count = event.params?.count ?? 0;
    console.log(\`
🔍 [SDK Event]
Name: \${event.eventName}
Category: \${event.eventCategory}
Timestamp: \${event.timestamp}
Count: \${count}
Params: \${JSON.stringify(event.params, null, 2)}
SDK Version: \${event.aaSdkVersion}
    \`);
  }
};

// Add only in debug builds
if (__DEV__) {
  Finvu.addEventListener(debugEventListener);
}`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Event Data Structure */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Event Data Structure
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: "#555" }}>
                Each event (FinvuEvent) contains the following properties:
            </Typography>
            <CopyBlock
                text={`interface FinvuEvent {
  eventName: string;          // Event name (e.g., "LOGIN_OTP_VERIFIED")
  eventCategory: string;      // Event category (e.g., "AUTHENTICATION")
  timestamp: string;         // ISO 8601 timestamp
  aaSdkVersion: string;       // SDK version
  params?: Record<string, any>; // Event parameters (sanitized, no PII)
  
  // Note: count is stored in params?.count (number) - event occurrence count
}

interface FinvuEventDefinition {
  category: string;           // Required: Event category
  stage?: string;             // Optional: Stage identifier
  fipId?: string;             // Optional: FIP ID
  fips?: string[];            // Optional: FIP names array
  fiTypes?: string[];          // Optional: FI types array
}`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            {/* Best Practices */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Best Practices
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listeners when done: Always remove event listeners in useEffect cleanup to prevent memory leaks.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use separate listeners for separation of concerns: Create separate listeners for analytics, debugging, and business logic.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Sanitize custom event parameters: The SDK automatically sanitizes parameters, but avoid including PII in custom events.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Handle events efficiently: Event listeners are called synchronously. For heavy processing, use async operations or background tasks.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events for business metrics: Use custom events to track user actions and business-specific metrics.</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use optional chaining for parameter access: Use <code>event.params?.key</code> to safely access parameters and avoid runtime errors.</Typography></li>
            </Box>

            {/* Troubleshooting */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Troubleshooting
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                Not receiving events?
            </Typography>
            <Typography sx={{ mt: 1, mb: 2, color: "#555" }}>
                Check if events are enabled:
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

// Ensure events are enabled
Finvu.setEventsEnabled(true);`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#555" }}>
                Verify listener is added:
            </Typography>
            <CopyBlock
                text={`import Finvu, { FinvuEvent } from '@cookiejar-technologies/finvu-react-native-sdk';

// Ensure listener is added before events occur
const eventListener = (event: FinvuEvent) => {
  // Handle event
};
Finvu.addEventListener(eventListener);`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography sx={{ mt: 3, mb: 2, color: "#555" }}>
                Check SDK connection:
            </Typography>
            <CopyBlock
                text={`import Finvu from '@cookiejar-technologies/finvu-react-native-sdk';

// Events may not fire if SDK is not connected
// Check your SDK initialization and connection status`}
                language="typescript"
                showLineNumbers={true}
                theme={nord}
            />

            <Typography variant="subtitle1" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Memory leaks?
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Always remove listeners when they're no longer needed</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listeners in useEffect cleanup function</Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Use cleanup functions in React components</Typography></li>
            </Box>

            {/* Summary */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Summary
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Enable tracking: <code>Finvu.setEventsEnabled(true)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Add listener: <code>Finvu.addEventListener(listener)</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Remove listener: <code>Finvu.removeEventListener()</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Register custom events: <code>{`Finvu.registerCustomEvents({'EVENT_NAME': {category: 'category'}})`}</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Track custom events: <code>{`Finvu.track('EVENT_NAME', {param: 'value'})`}</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Register event aliases: <code>{`Finvu.registerAliases({'EVENT_NAME': 'alias_name'})`}</code></Typography></li>
                <li><Typography sx={{ color: "#555", lineHeight: 1.7 }}>Available events: See event list above for all standard events</Typography></li>
            </Box>
        </Box>
    );
};

